// src/components/modules/Settings.js

import React, { useState, useMemo, useEffect } from "react";
import { Modal } from "../../common/Modal";
import CustomTable from "../../common/CustomTable";
import {
  Skeleton,
  Input,
  Select,
  message,
  Form,
  Button,
  DatePicker,
  Table,
  Spin,
  Modal as AntdModal,
  Dropdown,
  Space,
  InputNumber,
  Checkbox, Tooltip, Popover
} from "antd";
import { FaRegEdit, FaSave } from "react-icons/fa";
import { FaTrashAlt } from "react-icons/fa";
import axios from "axios";
import { data } from "autoprefixer";
import * as moment from "moment";
import { Create } from "@mui/icons-material";
import TextArea from "antd/es/input/TextArea";
import { Timesheetapi } from "../../../middleware/interceptor";
import { useMsal } from "@azure/msal-react";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ExclamationCircleFilled, DownOutlined } from "@ant-design/icons";
import { fontSize } from "@mui/system";

const { RangePicker } = DatePicker;


const MemberManagerModal = ({ projectId, memberIds, Employee, leadId, onClose, onRefresh, Timesheetapi, toast }) => {
  const [currentMembers, setCurrentMembers] = useState(memberIds || []);
  const [selectedToAdd, setSelectedToAdd] = useState([]);  // ← array now
  const [loading, setLoading] = useState(false);

  const availableToAdd = (Employee || []).filter(
    (emp) => !currentMembers.map(Number).includes(Number(emp.value))
  );

  const handleRemove = async (idToRemove) => {
    const updated = currentMembers.filter((id) => Number(id) !== Number(idToRemove));
    setLoading(true);
    try {
      await Timesheetapi.post(`api/Projects/UpdateProjectMembers`, {
        Project_id: projectId,
        Member_Ids: updated,
        Approvar_id: leadId,
        Emp_Name: localStorage.getItem("employee_id"),  // Include Lead_Id in the payload
      });
      setCurrentMembers(updated);
      onRefresh();
      toast.success("Member removed");
    } catch (e) {
      toast.error("Failed to remove member");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedToAdd || selectedToAdd.length === 0) return;
    const updated = [...new Set([...currentMembers.map(Number), ...selectedToAdd.map(Number)])];
    setLoading(true);
    try {
      await Timesheetapi.post(`api/Projects/UpdateProjectMembers`, {
        Project_id: projectId,
        Member_Ids: updated,
        Approvar_id: leadId,
        Emp_Name: localStorage.getItem("employee_id"),
      });
      setCurrentMembers(updated);
      setSelectedToAdd([]);  // clear after add
      onRefresh();
      toast.success(`${selectedToAdd.length} member(s) added`);
    } catch (e) {
      toast.error("Failed to add member");
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = (id) => {
    const emp = (Employee || []).find((e) => Number(e.value) === Number(id));
    return emp ? emp.label : null;
  };

  const visibleMembers = currentMembers.filter((id) => getMemberName(id) !== null);

  return (
    <Spin spinning={loading}>
      <div style={{ minWidth: 300 }}>
        <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 16 }}>
          {visibleMembers.length === 0 && (
            <p className="text-gray-400 text-[13px] text-center py-4">No members assigned</p>
          )}
          {visibleMembers.map((id) => (
            <div
              key={id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "7px 10px",
                borderBottom: "1px solid #f0f0f0",
                fontSize: 13,
              }}
            >
              <span>👤 {getMemberName(id)}</span>
              <span
                onClick={() => handleRemove(id)}
                style={{
                  cursor: "pointer",
                  color: "#ff4d4f",
                  fontWeight: 700,
                  fontSize: 16,
                  lineHeight: 1,
                  padding: "0 4px",
                }}
                title="Remove member"
              >
                ×
              </span>
            </div>
          ))}
        </div>

        {/* Multi-select add row */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Select
            showSearch
            allowClear
            mode="multiple"              // ← this is the key change
            placeholder="Add employees..."
            style={{ flex: 1 }}
            optionFilterProp="label"
            value={selectedToAdd}
            onChange={(val) => setSelectedToAdd(val)}
            options={availableToAdd}
            maxTagCount={2}              // shows "John, Jane +1 more"
          />
          <Button
            type="primary"
            onClick={handleAdd}
            disabled={selectedToAdd.length === 0}  // ← changed from !selectedToAdd
          >
            Add
          </Button>
        </div>
      </div>
    </Spin>
  );
};


const TaskMemberManagerModal = ({ taskId, memberIds, Employee, projectMembers, onRefresh, Timesheetapi, toast }) => {
  const [currentMembers, setCurrentMembers] = useState(memberIds || []);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [loading, setLoading] = useState(false);

  // Only show project members in the "add" dropdown (task members must be a subset of project members)
  const availableToAdd = (Employee || []).filter(
    (emp) =>
      !currentMembers.map(Number).includes(Number(emp.value)) &&
      (projectMembers || []).map(Number).includes(Number(emp.value))
  );

  const getMemberName = (id) => {
    const emp = (Employee || []).find((e) => Number(e.value) === Number(id));
    return emp ? emp.label : null;
  };

  const visibleMembers = currentMembers.filter((id) => getMemberName(id) !== null);

  const handleRemove = async (idToRemove) => {
    const updated = currentMembers.filter((id) => Number(id) !== Number(idToRemove));
    setLoading(true);
    try {
      await Timesheetapi.post(`api/Projects/UpdateTaskMembers`, {
        Item_Id: taskId,
        Member_Ids: updated,
      });
      setCurrentMembers(updated);
      onRefresh();
      toast.success("Member removed");
    } catch (e) {
      toast.error("Failed to remove member");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedToAdd || selectedToAdd.length === 0) return;
    const updated = [...new Set([...currentMembers.map(Number), ...selectedToAdd.map(Number)])];
    setLoading(true);
    try {
      await Timesheetapi.post(`api/Projects/UpdateTaskMembers`, {
        Item_Id: taskId,
        Member_Ids: updated,
      });
      setCurrentMembers(updated);
      setSelectedToAdd([]);
      onRefresh();
      toast.success(`${selectedToAdd.length} member(s) added`);
    } catch (e) {
      toast.error("Failed to add member");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <div style={{ minWidth: 300 }}>
        <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 16 }}>
          {visibleMembers.length === 0 && (
            <p className="text-gray-400 text-[13px] text-center py-4">No members assigned</p>
          )}
          {visibleMembers.map((id) => (
            <div
              key={id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "7px 10px",
                borderBottom: "1px solid #f0f0f0",
                fontSize: 13,
              }}
            >
              <span>👤 {getMemberName(id)}</span>
              <span
                onClick={() => handleRemove(id)}
                style={{
                  cursor: "pointer",
                  color: "#ff4d4f",
                  fontWeight: 700,
                  fontSize: 16,
                  lineHeight: 1,
                  padding: "0 4px",
                }}
                title="Remove member"
              >
                ×
              </span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Select
            showSearch
            allowClear
            mode="multiple"
            placeholder="Add members..."
            style={{ flex: 1 }}
            optionFilterProp="label"
            value={selectedToAdd}
            onChange={(val) => setSelectedToAdd(val)}
            options={availableToAdd}
            maxTagCount={2}
          />
          <Button
            type="primary"
            onClick={handleAdd}
            disabled={selectedToAdd.length === 0}
          >
            Add
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export const Project = ({ isAuthenticated }) => {
  // Loading state for project counts
  const [countsLoading, setCountsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenclient, setIsModalOpenclient] = useState(false);
  const [clientfilteroptions, setclientfilteroptions] = useState([]);
  const [clientfiltervalue, setclientfiltervalue] = useState(["All"]);
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [tempdata, settempData] = useState([]);
  const [errors, setErrors] = useState({});
  const [project, setProject] = useState([]);
  const [form] = Form.useForm();
  const [Clients] = Form.useForm();
  const [projectload, setprojectload] = useState(false);
  const [projectdeletemodal, setprojectdeletemodal] = useState(false);
  const [deletedata, setdeletedata] = useState();
  const [edit, setedit] = useState(false);
  const [user, setUser] = useState(null);
  const [projid, setprojid] = useState();
  const { instance } = useMsal();
  const [Employee, setEmployee] = useState();
  const [selectedClient, setSelectedClient] = useState();
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedAssignedToMembers, setSelectedAssignedToMembers] = useState([]);

  const [assignto, setAssignto] = useState([]);
  const [selectedassignto, setAssselectedignto] = useState([]);

  // Date filter state
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("month");
  const [selectedDates, setSelectedDates] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);
  const [selectedWeekDates, setSelectedWeekDates] = useState([]);
  const [sdate, setSdate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [edate, setEdate] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));

  // Add these states
  const [taskDeleteModal, setTaskDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);


  const [taskSearchText, setTaskSearchText] = useState("");

  // Task List: which tasks are shown + bulk selection
  const [taskView, setTaskView] = useState("open");          // "open" = Open + In Progress, "completed" = Completed
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [bulkTaskLoading, setBulkTaskLoading] = useState(false);

  const [currentPlannerType, setCurrentPlannerType] = useState(1);

  const [memberPopover, setMemberPopover] = useState({ visible: false, projectId: null, members: [] });

  // Flatpickr for week selection
  // Flatpickr is dynamically imported to avoid SSR issues
  let Flatpickr = null;
  if (typeof window !== "undefined") {
    Flatpickr = require("react-flatpickr").default;
    require("flatpickr/dist/flatpickr.min.css");
  }

  // Search filter state
  const [searchText, setSearchText] = useState("");


  const [MemberIds, setMemberIds] = useState();

  const [showtask, setshowtask] = useState(false);
  const [isProjectHoursMode, setIsProjectHoursMode] = useState(false);
  // Project Planner state
  const [isProjectPlanner, setIsProjectPlanner] = useState(false);
  const [trackList, setTrackList] = useState([]);
  const [moduleList, setModuleList] = useState([]);
  const [taskListOptions, setTaskListOptions] = useState([]);
  const [allTrackList, setAllTrackList] = useState([]);   // for preview labels
  const [allModuleList, setAllModuleList] = useState([]); // for preview labels
  const [taskListPreview, setTaskListPreview] = useState([]);
  const [taskListPreviewLoading, setTaskListPreviewLoading] = useState(false);
  const [previewSelectedTrackIds, setPreviewSelectedTrackIds] = useState([]);
  const [previewSelectedModuleId, setPreviewSelectedModuleId] = useState(null);
  const [selectedTaskListItems, setSelectedTaskListItems] = useState([]); // Track selected tasks for creation
  const [tablesLoading, setTablesLoading] = useState(false);
  // Track Project_type in state so "Add Sales" button re-renders reactively
  const [currentProjectType, setCurrentProjectType] = useState(1);
  const [projectHours, setProjectHours] = useState([]);
  const [projectHoursForm] = Form.useForm();
  const [editingProjectHourIndex, setEditingProjectHourIndex] = useState(null);



  const [tasks, settasks] = useState([]);
  const [TeamLead, setTeamLead] = useState(0);
  const [task_Id, settaskId] = useState(0);
  const [totalTaskHours, setTotalTaskHours] = useState(0);
  const [totalProjectCount, setTotalProjectCount] = useState(0);
  const [openProjectCount, setOpenProjectCount] = useState(0);
  const [closedProjectCount, setClosedProjectCount] = useState(0);
  const [newProject, setNewProject] = useState({
    projectName: "",
    client: "",
    status: "Active",
    clientid: "",
    assignedTo: "",
  });
  const [projectPlanning, setProjectPlanning] = useState(false);


  const getPlannerType = (clientLabel) => {
    if (!clientLabel) return null;
    const name = clientLabel.toLowerCase().trim();
    if (name.includes("freshwork")) return { type: 1, label: "Project Planner" };
    if (name.includes("rck")) return { type: 2, label: "HR Planner" };
    if (name.includes("market")) return { type: 3, label: "App Planner" };
    return null;
  };

  const success = (msg) => {
    messageApi.open({
      type: "success",
      title: "Success",
      content: msg,
      duration: 1.5,
    });
  };
  const error = (msg) => {
    messageApi.open({
      type: "error",
      duration: 1.5,
      content: msg,
    });
  };
  const warning = () => {
    messageApi.open({
      type: "warning",
      title: "Warning",
      duration: 1.5,
      content: "No more project available",
    });
  };

  // Calculate total task hours from the tasks array
  // const calculateTotalTaskHours = (tasksList) => {
  //   if (!tasksList || tasksList.length === 0) return 0;
  //   return tasksList.reduce((total, task) => {
  //     const hours = Number(task.Estimated_Hours || task.Planned_Hours || 0);
  //     return total + hours;
  //   }, 0);
  // };

  const calculateTotalTaskHours = (tasksList) => {
    if (!tasksList || tasksList.length === 0) return 0;
    // Only count Task List items (Sales_type=0) — Add Sales hours are already
    // included in Project_hours directly and must not be double-counted here
    return tasksList
      .filter((task) => Number(task.Sales_type) === 0)
      .reduce((total, task) => {
        const hours = Number(task.Estimated_Hours || task.Planned_Hours || 0);
        return total + hours;
      }, 0);
  };

  // Validate if new estimated hours will exceed project hours (Create Task only)
  const validateEstimatedHours = (estimatedHours, projectHours) => {
    const newEstimateValue = Number(estimatedHours) || 0;
    const projectHoursValue = Number(projectHours) || 0;
    const currentTotal = calculateTotalTaskHours(tasks);

    if (task_Id > 0 && tasks) {
      const currentTask = tasks.find(t => t.ItemId === task_Id);
      if (currentTask) {
        const currentTaskHours = Number(currentTask.Estimated_Hours || currentTask.Planned_Hours || 0);
        return currentTotal - currentTaskHours + newEstimateValue <= projectHoursValue;
      }
    }

    return currentTotal + newEstimateValue <= projectHoursValue;
  };

  // Get remaining hours available
  const getRemainingHours = (projectHours) => {
    const projectHoursValue = Number(projectHours) || 0;
    const currentTotal = calculateTotalTaskHours(tasks);
    const remaining = projectHoursValue - currentTotal;
    return Math.max(0, remaining);
  };



  // Open delete confirmation modal for task
  const toggleTaskDelete = (task) => {
    setTaskToDelete(task);
    setTaskDeleteModal(true);
  };

  // Confirm task deletion
  // const confirmTaskDelete = async () => {
  //   if (!taskToDelete) return;

  //   try {
  //     setprojectload(true);

  //     const response = await Timesheetapi.post(
  //       `api/Projects/DeleteTask?id=${taskToDelete.ItemId}`
  //     );

  //     if (response.data?.Msg === "Task deleted successfully" || 
  //         response.data?.toLowerCase().includes("success")) {

  //       toast.success("Task deleted successfully");

  //       // Refresh the modal with latest data
  //       await refreshModalData(projid);

  //     } else {
  //       toast.error("Failed to delete task");
  //     }
  //   } catch (error) {
  //     console.error("Error deleting task:", error);
  //     toast.error("Error deleting task");
  //   } finally {
  //     setprojectload(false);
  //     setTaskDeleteModal(false);
  //     setTaskToDelete(null);
  //   }
  // };


  // ✅ BEST & FINAL VERSION - confirmTaskDelete
  const confirmTaskDelete = async () => {
    if (!taskToDelete || !taskToDelete.ItemId) {
      toast.error("Invalid task selected");
      return;
    }

    const taskIdToDelete = taskToDelete.ItemId;   // save it before clearing

    try {
      setprojectload(true);

      // Call Delete API
      await Timesheetapi.post(
        `api/Projects/DeleteWorkItem?Item_Id=${taskIdToDelete}`
      );

      toast.success("Task deleted successfully");

      // Step 1: Immediately remove from UI (Optimistic Update)
      settasks((prev) => prev.filter((task) => task.ItemId !== taskIdToDelete));

      // Step 2: Close delete modal
      setTaskDeleteModal(false);
      setTaskToDelete(null);

      // Step 3: Refresh the project data to sync with backend
      if (projid) {
        await refreshModalData(projid);
      }

      // Step 4: Refresh main project list
      await getprojectdetails();

    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete task. Please try again.");
    } finally {
      setprojectload(false);
    }
  };


  // Cancel task deletion
  const cancelTaskDelete = () => {
    setTaskDeleteModal(false);
    setTaskToDelete(null);
  };



  const applyFilters = () => {
    let filtered = [...tempdata]; // start from your raw unfiltered data

    // 1. Client Filter (existing logic)
    const filterValue = Array.isArray(clientfiltervalue)
      ? clientfiltervalue[0]
      : clientfiltervalue;

    if (filterValue && filterValue !== "All") {
      filtered = filtered.filter((item) => item.client_name === filterValue);
    }

    // 2. Time Period Filter (WEEK)
    if (selectedTimePeriod === "week" && selectedWeekDates.length === 2) {
      const weekStart = dayjs(selectedWeekDates[0]).startOf("day");
      const weekEnd = dayjs(selectedWeekDates[1]).endOf("day");
      filtered = filtered.filter((item) => {
        // Use project start_date/end_date for filtering
        const projectStart = item.start_date ? dayjs(item.start_date) : null;
        const projectEnd = item.end_date ? dayjs(item.end_date) : null;
        // Project is included if it overlaps with the selected week
        if (!projectStart || !projectEnd) return false;
        return (
          (projectStart.isSameOrBefore(weekEnd) && projectEnd.isSameOrAfter(weekStart))
        );
      });
    }

    // 3. NEW: Global Search Filter
    if (searchText.trim()) {
      const query = searchText.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        return (
          (item.projectname || "").toLowerCase().includes(query) ||
          (item.client_name || "").toLowerCase().includes(query) ||
          (item.status === 1 ? "open" : item.status === 3 ? "closed" : "deleted").includes(query) ||
          (item.Project_type === 1 ? "ops" : item.Project_type === 2 ? "onetime" : "annual").includes(query)
        );
      });
    }

    setRawData(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [searchText, clientfiltervalue, tempdata]);

  useEffect(() => {
    if (isAuthenticated) {
      const { name, username } = instance?.getActiveAccount();
      console.log("username", name, username);
      setUser(username);
      fetchClientNames(username);
      getallemployee();
    }
  }, [isAuthenticated]);

  // Update balance hours when project hours or tasks change
  useEffect(() => {
    const projectHours = form.getFieldValue("Project_hours");
    const originalBalance = MemberIds?.Original?.Balance_hours;

    if (projectHours) {
      if (edit && originalBalance !== undefined) {
        form.setFieldValue("Balance_hours", originalBalance);
      } else if (!edit) {
        const balance = getRemainingHours(projectHours);
        form.setFieldValue("Balance_hours", balance);
      }
    }
  }, [tasks, form, edit, MemberIds]);

  const handleTimePeriodChange = (value) => {
    setSelectedTimePeriod(value);
    const today = dayjs();

    if (value === "day") {
      setSelectedDates([today, today]);
      setSdate(today.format("YYYY-MM-DD"));
      setEdate(today.format("YYYY-MM-DD"));
    } else if (value === "week") {
      setSelectedWeekDates([]);
      setSelectedDates([]);
      setSdate("");
      setEdate("");
    } else if (value === "month") {
      setSelectedDates([today.startOf("month"), today.endOf("month")]);
      setSdate(today.startOf("month").format("YYYY-MM-DD"));
      setEdate(today.endOf("month").format("YYYY-MM-DD"));
    } else if (value === "year") {
      setSelectedDates([today.startOf("year"), today.endOf("year")]);
      setSdate(today.startOf("year").format("YYYY-MM-DD"));
      setEdate(today.endOf("year").format("YYYY-MM-DD"));
    }
  };

  // Week selection handler for Flatpickr (ProjectReport.js style)
  const handleWeekSelection = (selectedDates_, instance) => {
    // Fix: Only call close if it exists (Flatpickr React wrapper may not provide it)
    if (instance && typeof instance.close === "function") {
      instance.close();
    }
    if (selectedDates_ && selectedDates_.length > 0) {
      const selectedTempDate = selectedDates_[0];
      selectedTempDate.setHours(0, 0, 0, 0);
      const startOfWeek = new Date(selectedTempDate);
      const dayOfWeek = startOfWeek.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startOfWeek.setDate(startOfWeek.getDate() - daysToMonday);
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(0, 0, 0, 0);
      setSelectedWeekDates([startOfWeek, endOfWeek]);
      setSdate(dayjs(startOfWeek).format("YYYY-MM-DD"));
      setEdate(dayjs(endOfWeek).format("YYYY-MM-DD"));
    }
  };

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const startOfPeriod = dates[0].startOf(selectedTimePeriod).format("YYYY-MM-DD");
      const endOfPeriod = dates[1].endOf(selectedTimePeriod).format("YYYY-MM-DD");
      setSelectedDates(dates);
      setSdate(startOfPeriod);
      setEdate(endOfPeriod);
    }
  };

  // const getallemployee = async () => {
  //   await Timesheetapi.post(`api/Projects/GetAllEmployee`)
  //     .then((Responce) => {
  //       setEmployee(Responce?.data?.Empdetails);
  //     })
  //     .catch((err) => { });
  // };


  const getallemployee = async () => {
    try {
      const empRes = await Timesheetapi.get("api/User/getAllEmployees");
      const dbEmployees = (empRes?.data?.Employees || []).filter(
        (e) => e.Del_emp_status == null && e.employee_role !== "Support Staff"
      );

      if (dbEmployees.length === 0) {
        setEmployee([]);
        return;
      }
      const emails = dbEmployees
        .filter((e) => e.employee_email)
        .map((e) => e.employee_email);

      let statusMap = {};
      try {
        const azureRes = await Timesheetapi.post("api/User/getAzureStatusBatch", { emails });
        if (azureRes.data?.success) {
          (azureRes.data.data || []).forEach((item) => {
            statusMap[item.email] = item.status;
          });
        }
      } catch (err) {
        console.error("Azure status check failed on Projects page:", err);
      }

      const azureCheckWorked = Object.keys(statusMap).length > 0;
      const activeEmployees = dbEmployees.filter((e) => {
        if (!e.employee_email) return false;
        if (!azureCheckWorked) return true;
        return statusMap[e.employee_email] === "Active";
      });

      const options = activeEmployees
        .map((e) => ({ value: e.employee_id, label: e.employee_name }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setEmployee(options);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
      setEmployee([]);
    }
  };

  const toggleModalclick = () => {
    setIsModalOpen(true);
    setTaskView("open");
    setSelectedTaskIds([]);
    setshowtask(false);
    setedit(false);
    settasks([]);
    settaskId(0);
    setProjectHours([]);
    setEditingProjectHourIndex(null);
    setCurrentProjectType(1); // default OPS
    setProjectPlanning(false);
    setIsProjectPlanner(false);
    setTaskListPreviewLoading(false);
    setSelectedClient(null);
    setSelectedTaskListItems([]); // Reset selected tasks for creation
    setTaskSearchText("");
    projectHoursForm.resetFields();
    form.resetFields();
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    console.log("toggel modal changed", !isModalOpen);
  };

  useEffect(() => {
    console.log("modal changed", isModalOpen);
  }, [isModalOpen]);

  const toggleModalclient = () => {
    setIsModalOpenclient(!isModalOpenclient);
  };

  const toggledelete = async (row) => {
    setprojectdeletemodal(true);
    setdeletedata(row.original.Project_id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const toggleModals = (row) => {
    setIsModalOpen(true);
    setTaskView("open");
    setSelectedTaskIds([]);
    setshowtask(false);
    let startdate = formatDate(row.original.start_date);
    let enddate = formatDate(row.original.end_date);

    console.log("row", row.original);
    setedit(true);
    setprojid(row.original.Project_id);
    form.setFieldsValue({
      Client: row.original.client_id,
      client_id: row.original.client_id,
      Charge: row?.original?.chargeability,
      Project: row.original.projectname,
      start_date: row.original.start_date == null ? "" : dayjs(startdate, "DD-MM-YYYY"),
      end_date: row.original.end_date == null ? "" : dayjs(enddate, "DD-MM-YYYY"),
      status: row.original.status,
      status_id: row.original.status_ID,
      Team_Lead: row.original.Lead_Id,
      Members: row.original.Project_Members,
      Project_type: row.original.Project_type || 1,
      Project_hours: row.original.Project_hours,
      Project_description: row.original.Project_description || "",
      Project_Planner: row.original.Project_Planner || 0,
    });

    setMemberIds({
      MemberIds: row.original.Project_Members,
      Original: row.original,
    });
    setTeamLead(row.original.Lead_Id);
    setSelectedAssignedToMembers(row.original.Project_Members);
    setCurrentProjectType(row.original.Project_type || 1);

    // Initialize Project Planner state based on loaded value
    // const plannerEnabled = (row.original.Project_Planner === 1);
    // setIsProjectPlanner(plannerEnabled);
    // if (plannerEnabled) {
    //   fetchTracks();
    //   fetchAllTaskList();
    // }

    const plannerEnabled = (row.original.Project_Planner === 1);
    setIsProjectPlanner(plannerEnabled);
    if (plannerEnabled) {
      const clientLabel = client.find(c => c.value === row.original.client_id)?.label || "";
      const planner = getPlannerType(clientLabel);
      const pType = planner?.type || 1;
      setCurrentPlannerType(pType);
      fetchTracks(pType);
      fetchAllTaskList(pType);
    }

    setSelectedTaskListItems([]);

    let tasks = data.filter((item) => item.Project_id == row.original.Project_id);

    let filter = tasks[0]?.Tasks.map((item) => ({
      ...item,
      Status_Name:
        item.Status == 1
          ? "Open"
          : item.Status == 2
            ? "In Progress"
            : "Completed",
      Sales_type: Number(item.Sales_type) === 1 ? 1 : 0,
      Sales_No: item.Sales_No || null,
      Billing_Status: typeof item.Billing_Status === "string"
        ? (item.Billing_Status === "billable" ? 1 : 0)
        : (item.Billing_Status ?? 1),
    }));

    console.log("tasks", tasks, tasks[0]?.Tasks);

    if (tasks[0]?.Tasks?.length > 0) {
      settasks(filter);
    } else {
      settasks([]);
    }

    setProjectHours([]);
  };

  // ── Helper function to refresh modal data after update ──
  const refreshModalData = async (projectId) => {
    if (!projectId) return;
    setTablesLoading(true);
    try {
      // Fetch the full project list to ensure we get the latest data
      const response = await Timesheetapi.get(
        `api/Projects/GetProjects?Role_id=${localStorage.getItem(
          "emp_role_id"
        )}&empid=${localStorage.getItem("employee_id")}&sdate=${sdate}&edate=${edate}`
      );

      // Find the freshly updated project from the API response
      const freshProject = response?.data?.projects?.find(
        (p) => p.Project_id === projectId
      );

      if (!freshProject) {
        console.warn("Project not found after refresh", projectId);
        return;
      }

      // Update form fields with fresh data
      const start = freshProject.start_date
        ? dayjs(formatDate(freshProject.start_date), "DD-MM-YYYY")
        : null;
      const end = freshProject.end_date
        ? dayjs(formatDate(freshProject.end_date), "DD-MM-YYYY")
        : null;

      form.setFieldsValue({
        Client: freshProject.client_id,
        Project: freshProject.projectname,
        start_date: start,
        end_date: end,
        status: freshProject.status,
        Project_type: freshProject.Project_type || 1,
        Project_hours: freshProject.Project_hours,
        Team_Lead: freshProject.Lead_Id,
        Members: freshProject.Project_Members,
        Project_description: freshProject.Project_description || "",
        Project_Planner: freshProject.Project_Planner || 0,
      });

      // Update tasks array → refreshes both "Added Sales" and "Task List" tables
      // const freshTasks = (freshProject.Tasks || []).map(item => ({
      //   ...item,
      //   Status_Name:
      //     item.Status === 1 ? "Open" :
      //     item.Status === 2 ? "In Progress" : "Completed",
      //   Sales_type: Number(item.Sales_type) === 1 ? 1 : 0,
      //   Sales_No: item.Sales_No || null,
      //   Billing_Status: typeof item.Billing_Status === "string"
      //     ? (item.Billing_Status === "billable" ? 1 : 0)
      //     : (item.Billing_Status ?? 1),
      // }));

      // settasks(freshTasks);

      // Update tasks array — IMPORTANT: Filter out soft-deleted tasks (Status = 0)
      const freshTasks = (freshProject.Tasks || [])
        .filter((item) => Number(item.Status) !== 0)     // ← This removes deleted tasks
        .map((item) => ({
          ...item,
          Status_Name:
            item.Status === 1 ? "Open" :
              item.Status === 2 ? "In Progress" : "Completed",
          Sales_type: Number(item.Sales_type) === 1 ? 1 : 0,
          Sales_No: item.Sales_No || null,
          Billing_Status: typeof item.Billing_Status === "string"
            ? (item.Billing_Status.toLowerCase() === "billable" ? 1 : 0)
            : (item.Billing_Status ?? 1),
        }));

      settasks(freshTasks);

      // Update related states with fresh data
      setCurrentProjectType(freshProject.Project_type || 1);
      setMemberIds({
        MemberIds: freshProject.Project_Members || [],
        Original: freshProject,
      });
      setTeamLead(freshProject.Lead_Id);
      setSelectedAssignedToMembers(freshProject.Project_Members);

      // Also update the main data array for consistencys
      await getprojectdetails();

    } catch (err) {
      console.error("Failed to refresh modal data", err);
    } finally {
      setTablesLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setprojectload(true);
    const formattedStartDate = dayjs(values.start_date).format("YYYY-MM-DD");

    // If this is a NEW Add Project Hours entry and the task end date exceeds the
    // current project end date, automatically extend the project end date to match.
    let formattedEndDate = dayjs(values.end_date).format("YYYY-MM-DD");
    if (
      isProjectHoursMode &&
      task_Id === 0 &&
      values.taskEndDate &&
      values.end_date
    ) {
      const taskEnd = dayjs(values.taskEndDate);
      const projectEnd = dayjs(values.end_date);
      if (taskEnd.isAfter(projectEnd)) {
        formattedEndDate = taskEnd.format("YYYY-MM-DD");
        // Also update the form field so the displayed end date reflects the change
        form.setFieldValue("end_date", taskEnd);
      }
    }

    // Also check all existing Add Project Hours tasks (those with salesType)
    // If any of them have end dates exceeding the project end date, extend it
    if (tasks && tasks.length > 0 && edit) {
      const projectHoursTasks = tasks.filter((t) => Number(t.Sales_type) === 1);
      projectHoursTasks.forEach((task) => {
        const taskEndDate = task.endDate || task.End_date;
        if (taskEndDate) {
          const taskEnd = dayjs(taskEndDate);
          const projectEnd = dayjs(formattedEndDate);
          if (taskEnd.isAfter(projectEnd)) {
            formattedEndDate = taskEnd.format("YYYY-MM-DD");
            form.setFieldValue("end_date", taskEnd);
          }
        }
      });
    }

    console.log(TeamLead, values?.Team_Lead, "ghgghg");

    // Determine task name:
    // Add Project Hours form → use Sales Type label as task name
    // Create Task form        → use Req_title as task name
    const salesTypeLabels = { 1: "Change request", 2: "SOW" };
    const taskName = isProjectHoursMode
      ? salesTypeLabels[values.salesType] || values.salesType
      : values.Req_title;

    // For Add Sales: update project hours by replacing old Sales hours with new value
    // Use != null check (not falsy) so that decreasing to 0 or a lower value still works
    let finalProjectHours = Number(values.Project_hours) || 0;
    if (isProjectHoursMode && values.Est_hours != null) {
      // If editing an existing Add Sales task, subtract the old hours first
      if (task_Id > 0) {
        const oldTask = tasks.find((t) => t.ItemId == task_Id);
        if (oldTask) {
          const oldHours = Number(oldTask.Estimated_Hours || oldTask.Planned_Hours) || 0;
          finalProjectHours = finalProjectHours - oldHours;
        }
      }
      // Add the new estimated hours (may be lower than old — that's fine)
      finalProjectHours = finalProjectHours + Number(values.Est_hours);
    }

    const payload = {
      projectName: values.Project,
      Chargeability: values?.Charge,
      Emp_Id: TeamLead,
      Projectid: MemberIds?.Original?.Project_id,
      client_id: values.Client,
      ClientName: "INKSCRIBE",
      assignedTo: localStorage.getItem("employee_id"),
      end_date: formattedEndDate,

      end_date_changed: edit ? (
        dayjs(values.end_date).format("YYYY-MM-DD") !==
        dayjs(MemberIds?.Original?.end_date).format("YYYY-MM-DD")
      ) : false,
      start_date: formattedStartDate,
      status: values.status,
      Item_Id: task_Id,
      Req_Id: 0,
      Role_id: 2,
      itemname: taskName,
      itemtype: "Task",
      assign_by: localStorage.getItem("employee_id"),
      assign_to:
        values?.Assign_To?.length > 0
          ? values.Assign_To.filter((items) => items != "All")
          : [],
      Assign_Members: values.Members,
      item_status: isProjectHoursMode ? 1 : (values.Task_Status || 1),  // Add Sales always Open
      itempriority: values.Item_Priority,


      // Ids: MemberIds?.MemberIds?.length > 0 ? MemberIds?.MemberIds : [],

      Ids: Array.isArray(MemberIds?.MemberIds) && MemberIds.MemberIds.length > 0
        ? MemberIds.MemberIds.map(Number).filter(Boolean)
        : Array.isArray(MemberIds?.Original?.Project_Members) && MemberIds.Original.Project_Members.length > 0
          ? MemberIds.Original.Project_Members.map(Number).filter(Boolean)
          : [],



      Est_hours: values.Est_hours,
      Sales_type: isProjectHoursMode ? 1 : 0,
      Start_data: values.taskStartDate
        ? dayjs(values.taskStartDate).format("YYYY-MM-DD")
        : null,
      End_date: values.taskEndDate
        ? dayjs(values.taskEndDate).format("YYYY-MM-DD")
        : null,
      Project_type: values.Project_type,
      Project_description: values.Project_description || "",
      Task_Description: !isProjectHoursMode ? (values.Task_Description || "") : "",

      // Billing_Status: !isProjectHoursMode ? (values.Billing_Status ?? 1) : "",
      Billing_Status: !isProjectHoursMode ? (form.getFieldValue("Billing_Status") ?? 1) : "",

      Project_Planner: values.Project_Planner ? 1 : 0,
      Task_List_ID: values.Task_List_ID || null,
      Track_ID: values.trackType || null,
      Module_ID: values.moduleType || null,
      Client_Involved: values.clientInvolved || null,
      Sign_Off_Required: values.signOffRequired || null,
      // Task_List_Items: isProjectPlanner ? selectedTaskListItems : [],
      Task_List_Items: selectedTaskListItems,

      Project_hours: finalProjectHours,
      tasks: tasks && tasks.length > 0 ? tasks : [],
      projectHoursEntries: values.Project_type === 1 ? projectHours : [],
    };

    if (isProjectHoursMode) {
      form.setFieldValue("Project_hours", finalProjectHours);
      if (MemberIds?.Original) {
        setMemberIds((prev) => ({
          ...prev,
          Original: {
            ...prev.Original,
            Project_hours: finalProjectHours,
          },
        }));
      }
    }

    console.log("DEBUG: tasks state before submit:", tasks);
    console.log("DEBUG: payload being sent:", payload);

    if (!edit) {
      console.log("payload", values);
      try {
        await Timesheetapi.post("api/Projects/CreateProjectWithTask", payload).then((response) => {
          console.log("response", response.data);
          if (response.data.Msg == "Project created successfully") {
            getprojectdetails();
            success(response.data.Msg);
            // Clear form and hide the sub-form
            form.resetFields();
            setSelectedTaskListItems([]); // Reset selected tasks
            setshowtask(false);
            setIsProjectHoursMode(false);
            toggleModal();
          }
        });
      } catch (error) {
        error("Error :", error);
      }

      //   try {
      //   const response = await Timesheetapi.post(
      //     "api/Projects/CreateProjectWithTask",
      //     payload
      //   );

      //   console.log("response", response.data);

      //   if (response.data.Msg === "Project created successfully") {
      //     await getprojectdetails();
      //     success(response.data.Msg);
      //     toggleModal();
      //   }

      // } catch (err) {
      //   console.error("Error:", err);
      // }


    }


    else {
      console.log("payload", MemberIds);
      try {
        const response = await Timesheetapi.post(
          "api/Projects/UpdateProjectwithTask",
          payload
        ).then((response) => {
          console.log("response", response.data);


          // if (response.data.Msg == "Project updated successfully") {
          //   success(response.data.Msg);
          //   form.resetFields();
          //   setSelectedTaskListItems([]);
          //   setshowtask(false);
          //   setIsProjectHoursMode(false);
          //   refreshModalData(projid);
          // }

          if (response.data.Msg == "Project updated successfully") {
            success(response.data.Msg);
            form.resetFields();
            setSelectedTaskListItems([]);
            setshowtask(false);
            setIsProjectHoursMode(false);

            // ✅ CRITICAL FIX: Update MemberIds immediately after every save
            // So next update sends correct Ids (old list = current list)
            const currentMembers = (values.Members || selectedAssignedToMembers || [])
              .map(Number)
              .filter(Boolean);

            setMemberIds(prev => ({
              ...prev,
              MemberIds: currentMembers,
              Original: {
                ...prev?.Original,
                Project_Members: currentMembers,
              }
            }));

            // Also update selectedAssignedToMembers to stay in sync
            setSelectedAssignedToMembers(currentMembers);

            refreshModalData(projid);
          }


        });
      } catch (error) {
        error("Error :", error);
      }
    }


    setprojectload(false);



  };



  console.log("formattedStartDate", showtask);

  useEffect(() => {
    setCountsLoading(true);
    getprojectdetails().then(() => setCountsLoading(false));
  }, [sdate, edate]);

  // Update End Date field if any Add Project Hours task has a later end date
  useEffect(() => {
    if (edit && isModalOpen && tasks && tasks.length > 0) {
      const projectHoursTasks = tasks.filter((t) => Number(t.Sales_type) === 1);
      if (projectHoursTasks.length > 0) {
        const currentEndDate = form.getFieldValue("end_date");
        let maxEndDate = currentEndDate ? dayjs(currentEndDate) : null;

        // Find the latest end date among all Add Project Hours tasks
        projectHoursTasks.forEach((task) => {
          const taskEndDate = task.endDate || task.End_date;
          if (taskEndDate) {
            const taskEnd = dayjs(taskEndDate);
            if (!maxEndDate || taskEnd.isAfter(maxEndDate)) {
              maxEndDate = taskEnd;
            }
          }
        });

        // Update the form field if we found a later date
        if (maxEndDate && (!currentEndDate || maxEndDate.isAfter(dayjs(currentEndDate)))) {
          form.setFieldValue("end_date", maxEndDate);
        }
      }
    }
  }, [tasks, edit, isModalOpen]);

  const [client, setClient] = useState([]);

  // ── Project Planner: fetch Tracks ──
  // const fetchTracks = async () => {
  //   try {
  //     const res = await Timesheetapi.get("api/Projects/GetTracks");
  //     setTrackList((res.data.Tracks || []).map(t => ({ value: t.Track_ID, label: t.Track_Name })));
  //   } catch (e) { setTrackList([]); }
  // };

  const fetchTracks = async (plannerType = 1) => {
    try {
      const res = await Timesheetapi.get(`api/Projects/GetTracks?planner_type=${plannerType}`);
      setTrackList((res.data.Tracks || []).map(t => ({ value: t.Track_ID, label: t.Track_Name })));
    } catch (e) { setTrackList([]); }
  };

  // ── Project Planner: fetch Modules for selected Track ──


  // const fetchModules = async (trackId, isEditMode = false) => {
  //   setModuleList([]);
  //   setTaskListOptions([]);
  //   // Only reset form fields when user manually changes track (not when loading for edit)
  //   if (!isEditMode) {
  //     form.setFieldsValue({ moduleType: null, Task_List_ID: null, Req_title: null, Task_Description: null, Billing_Status: 1, clientInvolved: null, signOffRequired: null });
  //   }
  //   try {
  //     const res = await Timesheetapi.get(`api/Projects/GetModules?track_id=${trackId}`);
  //     setModuleList((res.data.Modules || []).map(m => ({ value: m.Module_ID, label: m.Module_Name })));
  //   } catch (e) { setModuleList([]); }
  // };


  const fetchModules = async (trackId, isEditMode = false, plannerType = 1) => {
    setModuleList([]);
    setTaskListOptions([]);
    if (!isEditMode) {
      form.setFieldsValue({
        moduleType: null, Task_List_ID: null, Req_title: null,
        Task_Description: null, Billing_Status: 1, clientInvolved: null, signOffRequired: null
      });
    }
    try {
      const res = await Timesheetapi.get(
        `api/Projects/GetModules?track_id=${trackId}&planner_type=${plannerType}`
      );
      setModuleList((res.data.Modules || []).map(m => ({ value: m.Module_ID, label: m.Module_Name })));
    } catch (e) { setModuleList([]); }
  };

  // ── Project Planner: fetch Task_List for selected Track + Module ──
  // const fetchTaskList = async (trackId, moduleId, isEditMode = false) => {
  //   setTaskListOptions([]);
  //   // Only reset form fields when user manually changes module (not when loading for edit)
  //   if (!isEditMode) {
  //     form.setFieldsValue({ Task_List_ID: null, Req_title: null, Task_Description: null, Billing_Status: 1, clientInvolved: null, signOffRequired: null });
  //   }
  //   try {
  //     const res = await Timesheetapi.get(`api/Projects/GetTaskList?track_id=${trackId}&module_id=${moduleId}`);
  //     setTaskListOptions(res.data.TaskList || []);
  //   } catch (e) { setTaskListOptions([]); }
  // };

  const fetchTaskList = async (trackId, moduleId, isEditMode = false, plannerType = 1) => {
    setTaskListOptions([]);
    if (!isEditMode) {
      form.setFieldsValue({
        Task_List_ID: null, Req_title: null, Task_Description: null,
        Billing_Status: 1, clientInvolved: null, signOffRequired: null
      });
    }
    try {
      const res = await Timesheetapi.get(
        `api/Projects/GetTaskList?track_id=${trackId}&module_id=${moduleId}&planner_type=${plannerType}`
      );
      setTaskListOptions(res.data.TaskList || []);
    } catch (e) { setTaskListOptions([]); }
  };

  // ── Project Planner: fetch ALL Task_List records for preview ──
  // const fetchAllTaskList = async () => {
  //   setTaskListPreviewLoading(true);
  //   try {
  //     const [taskRes, trackRes, moduleRes] = await Promise.all([
  //       Timesheetapi.get("api/Projects/GetAllTaskList"),
  //       Timesheetapi.get("api/Projects/GetAllTracks"),
  //       Timesheetapi.get("api/Projects/GetAllModules"),
  //     ]);
  //     setTaskListPreview(taskRes.data.TaskList || []);
  //     setAllTrackList(trackRes.data.Tracks || []);
  //     setAllModuleList(moduleRes.data.Modules || []);
  //   } catch (e) { 
  //     setTaskListPreview([]); 
  //   } finally {
  //     setTaskListPreviewLoading(false);
  //   }
  // };

  const fetchAllTaskList = async (plannerType = 1) => {
    setTaskListPreviewLoading(true);
    try {
      const [taskRes, trackRes, moduleRes] = await Promise.all([
        Timesheetapi.get(`api/Projects/GetAllTaskList?planner_type=${plannerType}`),
        Timesheetapi.get(`api/Projects/GetAllTracks?planner_type=${plannerType}`),
        Timesheetapi.get(`api/Projects/GetAllModules?planner_type=${plannerType}`),
      ]);
      setTaskListPreview(taskRes.data.TaskList || []);
      setAllTrackList(trackRes.data.Tracks || []);
      setAllModuleList(moduleRes.data.Modules || []);
    } catch (e) {
      setTaskListPreview([]);
    } finally {
      setTaskListPreviewLoading(false);
    }
  };



  // ── Project Planner: when a task is selected from Task_List, auto-fill fields ──
  const handleTaskListSelect = (taskListId) => {
    const selected = taskListOptions.find(t => t.Task_List_ID === taskListId);
    if (selected) {
      form.setFieldsValue({
        trackType: selected.Track_ID || form.getFieldValue('trackType'),
        moduleType: selected.Module_ID || form.getFieldValue('moduleType'),
        Task_List_ID: selected.Task_List_ID,
        Req_title: selected.Task_List_Name,// ← This fills the textarea
        clientInvolved: selected.Client_Involved != null ? String(selected.Client_Involved) : null,
        signOffRequired: selected.Sign_Off_Required != null ? String(selected.Sign_Off_Required) : null,
      });
    }
  };

  const getprojectdetails = async () => {
    try {
      setprojectload(true);

      const response = await Timesheetapi.get(`api/Projects/getclientname`);

      const clientOptions = response.data.Client.map((Projects) => ({
        value: Projects.Client_id,
        label: Projects.Client_name,
      })).filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value)
      );

      setClient(clientOptions);

      const respons = await Timesheetapi.get(
        `api/Projects/GetProjects?Role_id=${localStorage.getItem(
          "emp_role_id"
        )}&empid=${localStorage.getItem("employee_id")}&sdate=${sdate}&edate=${edate}`
      );

      // Fetch aggregated hours from getprojectlist and merge
      let hoursMap = {};
      try {
        const hoursResp = await Timesheetapi.get(
          `api/Projects/getprojectlist?Role_id=${localStorage.getItem(
            "emp_role_id"
          )}&empid=${localStorage.getItem("employee_id")}&sdate=${sdate}&edate=${edate}`
        );
        const projectsHours = hoursResp?.data?.Projects || [];
        projectsHours.forEach((p) => {
          hoursMap[p.project_id] = p;
        });
      } catch (err) {
        console.warn("Could not fetch project hours:", err);
      }

      const clientfilterOptions = respons.data.projects
        .map((Projects) => ({
          value: Projects.client_name,
          label: Projects.client_name,
        }))
        .filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.value === item.value)
        );

      const allOption = { value: "All", label: "All" };
      clientfilterOptions.unshift(allOption);

      setclientfilteroptions(clientfilterOptions);

      // OPS projects are a recurring monthly retainer, so their budgeted hours
      // scale with the number of months in the selected range (same convention
      // as "Approved Hours" on the Clients page). Onetime/Annual projects have
      // a fixed total budget and are left as-is.
      const monthDuration = dayjs(edate).diff(dayjs(sdate), "month") + 1;

      let data = respons?.data?.projects?.map((item) => {
        const hm = hoursMap[item.Project_id] || {};
        const baseProjectHours = Number(item.Project_hours) || 0;
        const isRecurringOps = item.Project_type === 1;
        const scaledProjectHours = isRecurringOps
          ? baseProjectHours * monthDuration
          : baseProjectHours;
        const workedHours = Number(hm.Worked_hours ?? item.Worked_hours ?? 0);

        return {
          ...item,
          status_ID: item.status,
          create_date: item.create_date ? moment(item.create_date).format("YYYY-MM-DD") : null,
          update_date: item.update_date ? moment(item.update_date).format("YYYY-MM-DD") : null,
          Project_hours: scaledProjectHours,
          Totalhours: hm.Totalhours ?? item.Totalhours ?? 0,
          Worked_hours: workedHours,
          Balance_hours: Math.round((scaledProjectHours - workedHours) * 100) / 100,
        };
      });

      // Sort data by project name alphabetically
      data = data.sort((a, b) => {
        const nameA = (a.projectname || "").toLowerCase();
        const nameB = (b.projectname || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      console.log("tytyty", data);

      const filteredstate = data?.filter((item) => item.client_name == clientfiltervalue);

      if (clientfiltervalue != "All") {
        setRawData(filteredstate);
      } else {
        setRawData(data);
      }

      settempData(data);
      setprojectload(false);
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  };

  const fetchClientNames = async (username) => { };

  const handleClientChange = (e) => {
    const { id, name } = JSON.parse(e.target.value);
    setSelectedClient({ id, name });
    console.log("id", e.target.value);
    setNewProject({ ...newProject, client: name, clientid: id });
  };

  const onChangeswitch = async (e, checked) => {
    console.log(e, checked);
    await Timesheetapi.post(
      `api/Projects/updateprojectstatus?id=${checked.Project_id}&status=${e ? 1 : 0}`
    )
      .then((response) => {
        const updatedData = data.map((item) => {
          if (item.Project_id == checked.Project_id) {
            item.status = e ? 1 : 0;
          }
          return item;
        });
        setRawData(updatedData);
      })
      .catch((error) => {
        console.error("Error updating project status:", error);
      });

    console.log("data2024", data);
  };

  const [editingKey, setEditingKey] = useState(null);
  const [editedName, setEditedName] = useState("");

  const toggleModalsclient = (row) => {
    setEditingKey(row.key);
    setEditedName(row.name);
  };

  const saveEdit = (rowKey) => {
    const newData = dataclient.map((row) => {
      if (row.key === rowKey) {
        return { ...row, name: editedName };
      }
      return row;
    });
    setEditingKey(null);
  };

  const columnsclient = [
    {
      title: "Client Name",
      dataIndex: "name",
      key: "name",
      render: (text, row) => {
        return editingKey === row.key ? (
          <Input value={editedName} onChange={(e) => setEditedName(e.target.value)} />
        ) : (
          text
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, row) => {
        return editingKey === row.key ? (
          <FaSave
            onClick={() => saveEdit(row.key)}
            className="text-[19.5px] text-[#3b82f6] cursor-pointer"
          />
        ) : (
          <>
            <FaRegEdit
              onClick={() => toggleModalsclient(row)}
              className="text-[19.5px] text-[#3b82f6] cursor-pointer"
            />
            <FaTrashAlt
              onClick={() => toggledelete(row)}
              className="text-[16px] text-[#ff4d4d] cursor-pointer ml-2"
            />
          </>
        );
      },
    },
  ];

  const dataclient = [
    { key: "1", name: "John Brown", age: 32, address: "New York No. 1 Lake Park" },
    { key: "2", name: "Jim Green", age: 42, address: "London No. 1 Lake Park" },
    { key: "3", name: "Joe Black", age: 32, address: "Sydney No. 1 Lake Park" },
    { key: "4", name: "Jim Red", age: 32, address: "London No. 2 Lake Park" },
  ];

  const [selectedStatus, setSelectedStatus] = useState(1);

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  useEffect(() => {
    if (selectedStatus != null && rawData) {
      let filteredProjects;
      if (selectedStatus === "All") {
        filteredProjects = rawData.filter((p) => Number(p.status) !== 2);
      } else {
        filteredProjects = rawData.filter(
          (p) => Number(p.status) === Number(selectedStatus)
        );
      }
      setData(filteredProjects);
    }
  }, [rawData, selectedStatus]);

  // Calculate project counts based on client filter
  // NOTE: Use rawData to always show complete counts, independent of status filter
  useEffect(() => {
    let projectsToCount = rawData;

    // If a specific client is selected, filter to that client only
    // Handle both array and string values for clientfiltervalue
    const filterValue = Array.isArray(clientfiltervalue) ? clientfiltervalue[0] : clientfiltervalue;

    if (filterValue && filterValue !== "All") {
      projectsToCount = rawData.filter((p) => p.client_name === filterValue);
    }

    // Calculate total, open, and closed project counts
    // Total = Open projects (status 1) + Closed projects (status 3) — exclude deleted (status 2)
    const total = projectsToCount.filter((p) => Number(p.status) === 1 || Number(p.status) === 3).length;
    const open = projectsToCount.filter((p) => Number(p.status) === 1).length;
    const closed = projectsToCount.filter((p) => Number(p.status) === 3).length;

    setTotalProjectCount(total);
    setOpenProjectCount(open);
    setClosedProjectCount(closed);
  }, [rawData, clientfiltervalue]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "client_name",
        header: "Clients",
        size: 150,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
      },
      {
        accessorKey: "projectname",
        header: "Project Name",
        size: 150,
        enableColumnActions: false,
        enableSorting: true,
        muiTableHeadCellProps: { align: "center" },
        Cell: ({ row }) => {
          const value = row.original.projectname;
          return value == null || value === "" ? "-" : value;
        },
      },
      {
        accessorKey: "start_date",
        header: "Start Date",
        size: 130,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { style: { textAlign: "left" } },
        Cell: ({ row }) => {
          const value = row.original.start_date;
          return value == null || value === "" ? "-" : value;
        },
      },
      {
        accessorKey: "end_date",
        header: "End Date",
        size: 100,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { style: { textAlign: "left" } },
        Cell: ({ row }) => {
          const value = row.original.end_date;
          return value == null || value === "" ? "-" : value;
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        enableSorting: false,
        enableColumnActions: false,
        size: 90,
        muiTableHeadCellProps: { align: "center" },
        Cell: ({ row }) => {
          const value = row.original.status;
          return value == 1 ? "Open" : value == 2 ? "Deleted" : "Closed";
        },
      },
      {
        accessorKey: "Project_type",
        header: "Project Type",
        size: 120,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        Cell: ({ row }) => {
          const value = row.original.Project_type;
          let typeLabel = "-";
          if (value === 1) typeLabel = "OPS";
          else if (value === 2) typeLabel = "Onetime";
          else if (value === 3) typeLabel = "Annual";
          return typeLabel;
        },
      },
      {
        accessorKey: "Project_hours",
        header: "Project Hours",
        size: 120,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { style: { textAlign: "right" } },
        Cell: ({ row }) => {
          const value = row.original.Project_hours;
          return value == null || value === "" ? "-" : value;
        },
      },
      {
        accessorKey: "Worked_hours",
        header: "Worked Hours",
        size: 120,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { style: { textAlign: "right" } },
        Cell: ({ row }) => {
          const totalMinutes = row.original.Totalhours ?? null;
          let displayValue = "-";

          if (totalMinutes != null && !isNaN(totalMinutes) && totalMinutes > 0) {
            displayValue = formatMinutesToHHMM(totalMinutes);
          } else {
            const workedDecimal = row.original.Worked_hours;
            if (workedDecimal != null && !isNaN(workedDecimal) && workedDecimal > 0) {
              const mins = Math.round(Number(workedDecimal) * 60);
              displayValue = formatMinutesToHHMM(mins);
            }
          }

          const handleWorkedHoursClick = () => {
            let projectName = row.original.projectname;
            const projectId = row.original.Project_id;
            const clientName = row.original.client_name;
            const clientId = row.original.client_id;

            // Ensure projectName is not empty/null/ID-like, if it looks like an ID, use projectId as fallback
            if (!projectName || projectName === projectId || /^\d+$/.test(String(projectName))) {
              projectName = `Project-${projectId}`;
            }

            console.log("Worked hours clicked - projectId:", projectId, "projectName:", projectName, "clientName:", clientName);

            // Open ProjectReport in new tab with project pre-selected and date range
            const reportUrl = `/reports?project=${encodeURIComponent(projectId)}&projectName=${encodeURIComponent(projectName)}&client=${encodeURIComponent(clientName)}&clientId=${encodeURIComponent(clientId)}&startDate=${encodeURIComponent(sdate)}&endDate=${encodeURIComponent(edate)}&timePeriod=${encodeURIComponent(selectedTimePeriod)}`;
            window.open(reportUrl, '_blank');
          };

          return displayValue !== "-" ? (
            <span
              onClick={handleWorkedHoursClick}
              className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
              title="Click to view project report"
            >
              {displayValue}
            </span>
          ) : (
            displayValue
          );
        },
      },
      {
        accessorKey: "Balance_hours",
        header: "Balance Hours",
        size: 120,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { style: { textAlign: "right" } },
        Cell: ({ row }) => {
          const apiBalance = row.original.Balance_hours;
          if (apiBalance != null && apiBalance !== "" && !isNaN(apiBalance)) {
            return Number(apiBalance);
          }

          const projectHours = Number(row.original.Project_hours) || 0;
          const projectEntry = tempdata.find((p) => p.Project_id === row.original.Project_id);

          const parseTaskHours = (task) => {
            if (task?.Hours != null && task.Hours !== "") return Number(task.Hours) || 0;
            const th = task?.Totalhours;
            if (th != null && th !== "") {
              if (typeof th === "number") return th > 24 ? th / 60 : th;
              if (typeof th === "string") {
                if (th.includes(":")) {
                  const [hh, mm] = th.split(":").map((v) => Number(v) || 0);
                  return hh + mm / 60;
                }
                const num = Number(th);
                return isNaN(num) ? 0 : num > 24 ? num / 60 : num;
              }
            }
            if (task["Task+Hours"] != null) return Number(task["Task+Hours"]) || 0;
            if (task.TaskHours != null) return Number(task.TaskHours) || 0;
            return 0;
          };

          let totalWorkedHours = 0;
          if (projectEntry && Array.isArray(projectEntry.Tasks)) {
            totalWorkedHours = projectEntry.Tasks.reduce((sum, t) => sum + parseTaskHours(t), 0);
          }

          const balanceHours = projectHours - totalWorkedHours;
          return balanceHours <= 0 ? 0 : Number(balanceHours.toFixed(2));
        },
      },

      {
        accessorKey: "Project_Members",
        header: "Members",
        size: 120,
        enableColumnActions: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { style: { textAlign: "center" } },
        Cell: ({ row }) => {


          const members = row.original.Project_Members;
          const memberIds = Array.isArray(members) ? members : [];
          //const count = memberIds.length;

          // if (count === 0) return <span>-</span>;

          // Get member names from Employee list
          // const memberNames = memberIds
          //   .map((id) => {
          //     const emp = Employee?.find((e) => e.value === id || e.value === Number(id));
          //     return emp ? emp.label : `Employee ${id}`;
          //   })
          //   .filter(Boolean);

          const memberNames = memberIds
            .map((id) => {
              const emp = Employee?.find((e) => e.value === id || e.value === Number(id));
              return emp ? emp.label : null;   // return null if not found
            })
            .filter(Boolean);  // removes nulls — hides unknown/inactive employees


          const count = memberNames.length;

          if (count === 0) return <span>-</span>;

          const tooltipContent = (
            <div style={{ maxWidth: 220 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>
                Team Members ({count})
              </div>
              {memberNames.map((name, idx) => (
                <div
                  key={idx}
                  style={{
                    fontSize: 12,
                    padding: "2px 0",
                    borderBottom: idx < memberNames.length - 1 ? "1px solid rgba(255,255,255,0.15)" : "none",
                  }}
                >
                  👤 {name}
                </div>
              ))}
            </div>
          );



          // return (
          //   <Tooltip title={tooltipContent} placement="left" color="#1d2a3a">
          //     <span
          //       style={{
          //         display: "inline-flex",
          //         alignItems: "center",
          //         justifyContent: "center",
          //         gap: 4,
          //         // background: "#fff",
          //         color: "#1a56db",
          //         borderRadius: 12,
          //         padding: "2px 10px",
          //         fontWeight: 500,
          //         fontSize: 12,
          //         cursor: "pointer",
          //         minWidth: 36,
          //       }}
          //     >

          //       {count}
          //     </span>
          //   </Tooltip>
          // );

          return (
            <Popover
              trigger="click"
              placement="left"
              content={
                <MemberManagerModal
                  projectId={row.original.Project_id}
                  memberIds={memberIds}
                  Employee={Employee}
                  leadId={row.original.Lead_Id}
                  onClose={() => { }}
                  onRefresh={getprojectdetails}
                  Timesheetapi={Timesheetapi}
                  toast={toast}
                />
              }
              title="Manage Members"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  color: "#1a56db",
                  borderRadius: 12,
                  padding: "2px 10px",
                  fontWeight: 500,
                  fontSize: 12,
                  cursor: "pointer",
                  minWidth: 36,
                  // ADD this to prevent any inherited hover background:
                  background: "transparent",
                }}
              >
                {count}
              </span>
            </Popover>
          );

        },
      },

      ...(localStorage.getItem("emp_role_id") === "3"
        ? [
          {
            accessorKey: "id",
            header: "Action",
            size: 80,
            enableColumnActions: false,
            muiTableHeadCellProps: { align: "center" },
            Cell: ({ row }) => (
              <div className="flex justify-center gap-3">
                <FaRegEdit
                  onClick={() => toggleModals(row)}
                  className={`text-[15px] ${row.original.status === 0
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-[#3b82f6] cursor-pointer"
                    }`}
                />
                <FaTrashAlt
                  onClick={() => toggledelete(row)}
                  className="text-[14px] text-[#ff4d4d] cursor-pointer"
                />
              </div>
            ),
          },
        ]
        : []),
    ],
    [data]
  );

  const handleChange = (selected) => {
    console.log(clientfilteroptions, "fgfgf");
    console.log(selected, "rwrwr");
    setclientfiltervalue(selected);
    if (selected == "All") {
      setRawData(tempdata);
    } else {
      const filtered = tempdata.filter((item) => item.client_name === selected);
      setRawData(filtered);
    }
  };

  const cancelDelete = () => {
    setprojectdeletemodal(false);
  };

  const confirmDelete = async () => {
    try {
      setprojectload(true);
      const response = await Timesheetapi.post(`api/Projects/deleteproject?id=${deletedata}`);

      console.log("response", response.data);

      if (response.data == "deleted successfully") {
        getprojectdetails();
        toast.success(response.data);
        setRawData((prevData) => prevData.filter((item) => item.id !== deletedata));
      }
    } catch (error) {
      console.error("Error fetching client names:", error);
    }

    setprojectload(false);
    setprojectdeletemodal(false);
  };

  const handleAssignedToChange = (value) => {
    const allValuesExceptAll = Employee.filter((m) => m.value !== "All").map((m) => m.value);

    if (value.includes("All")) {
      setSelectedAssignedToMembers(["All", ...allValuesExceptAll]);
      return;
    }

    setSelectedAssignedToMembers((prev) => {
      if (prev.includes("All") && !value.includes("All")) {
        return [];
      }
      return value;
    });
  };

  const HandleMembers = (e) => {
    setAssselectedignto(e);
    console.log("selectedAssignedToMembers", selectedAssignedToMembers);
  };

  useEffect(() => {
    if (selectedAssignedToMembers.length > 0) {
      let filter = Employee.filter((item) => selectedAssignedToMembers.includes(item.value));

      console.log(filter, "Filter");
      setAssignto([{ label: "All", value: "All" }, ...filter]);

      const filterValues = filter.map((item) => item.value);
      const filteredAssignto = selectedassignto
        .filter((val) => val !== "All")
        .filter((val) => filterValues.includes(val));

      const isEqual =
        filterValues.length === filteredAssignto.length &&
        filterValues.every((val) => filteredAssignto.includes(val)) &&
        filteredAssignto.every((val) => filterValues.includes(val));

      console.log(isEqual, "test isequal", filterValues, filteredAssignto);

      if (isEqual && filteredAssignto.length > 0) {
        setAssselectedignto(["All", ...filteredAssignto]);
        form.setFieldValue("Assign_To", ["All", ...filteredAssignto]);
        return;
      }
      setAssselectedignto(filteredAssignto);
      form.setFieldValue("Assign_To", filteredAssignto);
      return;
    } else {
      setAssignto([]);
      setAssselectedignto([]);
      form.setFieldValue("Assign_To", []);
    }
  }, [selectedAssignedToMembers]);

  // Reset Project Planner when client changes (only available for Freshwork)


  // useEffect(() => {
  //   if (isModalOpen && selectedClient) {
  //     const selectedClientLabel = client.find(c => c.value === selectedClient)?.label || "";
  //     const isFreshwork = selectedClientLabel.toLowerCase().includes("freshwork");

  //     if (!isFreshwork && isProjectPlanner) {
  //       // Client changed from Freshwork to something else, reset Project Planner
  //       setIsProjectPlanner(false);
  //       form.setFieldValue("Project_Planner", false);
  //       setTrackList([]);
  //       setModuleList([]);
  //       setTaskListOptions([]);
  //       setTaskListPreview([]);
  //       setTaskListPreviewLoading(false);
  //       setSelectedTaskListItems([]); // Reset selected tasks
  //       form.setFieldsValue({
  //         trackType: null,
  //         moduleType: null,
  //         Task_List_ID: null,
  //         clientInvolved: null,
  //         signOffRequired: null,
  //       });
  //     }
  //   }
  // }, [selectedClient, isModalOpen, client]);


  useEffect(() => {
    if (isModalOpen && selectedClient) {
      const selectedClientLabel = client.find(c => c.value === selectedClient)?.label || "";
      const planner = getPlannerType(selectedClientLabel);

      if (!planner && isProjectPlanner) {
        // Client has no planner — reset everything
        setIsProjectPlanner(false);
        form.setFieldValue("Project_Planner", false);
        setTrackList([]); setModuleList([]);
        setTaskListOptions([]); setTaskListPreview([]);
        setTaskListPreviewLoading(false);
        setSelectedTaskListItems([]);
        form.setFieldsValue({
          trackType: null, moduleType: null,
          Task_List_ID: null, clientInvolved: null, signOffRequired: null,
        });
      }
    }
  }, [selectedClient, isModalOpen, client]);


  const handleDeleteTask = (id) => {
    alert(id);
  };

  // Format total minutes (number) to H:MM string
  const formatMinutesToHHMM = (totalMinutes) => {
    if (totalMinutes == null || isNaN(totalMinutes)) return "-";
    const mins = parseInt(totalMinutes, 10);
    if (mins === 0) return "-";
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    return `${hours}:${String(minutes).padStart(2, "0")}`;
  };

  // const handleEditTask = async (id) => {
  //   let Tasks = tasks.find((item) => item.ItemId == id);

  //   console.log(MemberIds, "MemberIds");
  //   settaskId(id);

  //   // Check if this is a project hours task
  //   const isProjectTask = Number(Tasks.Sales_type) === 1;
  //   setIsProjectHoursMode(isProjectTask);

  //   // Set the basic task fields
  //   form.setFieldsValue({
  //     Req_title: Tasks.Task_name,
  //     Item_Priority: parseInt(Tasks.Priority),
  //     Task_Status: parseInt(Tasks.Status),
  //     Est_hours: Tasks.Planned_Hours,
  //   });

  //   // If this task has Track/Module values, load the dropdown options so they display correctly
  //   if (Tasks.Track_ID) {
  //     try {
  //       // First fetch all tracks to populate the Track dropdown
  //       await fetchTracks();
  //       // Store the module/task data to set after fetching
  //       const trackId = Tasks.Track_ID;
  //       const moduleId = Tasks.Module_ID;

  //       // Then fetch modules for the selected track
  //       await fetchModules(trackId);
  //       // After modules are loaded, if it also has a Module_ID, fetch the task list
  //       if (moduleId) {
  //         await fetchTaskList(trackId, moduleId);
  //       }

  //       // NOW set the planner field values (after data is loaded)
  //       form.setFieldsValue({
  //         trackType: trackId || null,
  //         moduleType: moduleId || null,
  //         clientInvolved: Tasks.Client_Involved || null,
  //         signOffRequired: Tasks.Sign_Off_Required || null,
  //       });
  //     } catch (error) {
  //       console.error("Error loading project planner data:", error);
  //     }
  //   }

  //   // Set project hours specific fields if applicable
  //   if (isProjectTask) {
  //     form.setFieldsValue({
  //       salesType: Tasks.Task_name === "Change request" ? 1 : Tasks.Task_name === "SOW" ? 2 : null,
  //       // Populate start/end dates from backend fields Start_data / End_date
  //       taskStartDate: Tasks.Start_data
  //         ? dayjs(Tasks.Start_data)
  //         : Tasks.startDate
  //         ? dayjs(Tasks.startDate)
  //         : null,
  //       taskEndDate: Tasks.End_date
  //         ? dayjs(Tasks.End_date)
  //         : Tasks.endDate
  //         ? dayjs(Tasks.endDate)
  //         : null,
  //     });
  //   }

  //   const tempComparer = JSON.parse(Tasks.Task_Members).map(Number);

  //   const selectedList = assignto.map((val) => val.value).filter((val) => val !== "All");
  //   const selectedMembers = tempComparer.filter((val) => selectedList.includes(val));

  //   const isEqual =
  //     selectedList.length === selectedMembers.length &&
  //     selectedList.every((val) => selectedMembers.includes(val)) &&
  //     selectedMembers.every((val) => selectedList.includes(val));

  //   console.log(`${tempComparer} => ${selectedMembers}`, selectedList, "MEmbers", isEqual);

  //   if (isEqual && selectedMembers.length > 0) {
  //     setAssselectedignto(["All", ...selectedMembers]);
  //     form.setFieldValue("Assign_To", ["All", ...selectedMembers]);
  //   } else {
  //     setAssselectedignto(selectedMembers);
  //     form.setFieldValue("Assign_To", selectedMembers);
  //   }
  //   setshowtask(true);
  // };

  const handleEditTask = async (id) => {
    const Tasks = tasks.find((item) => item.ItemId === id);
    if (!Tasks) return;

    settaskId(id);

    const isProjectTask = Number(Tasks.Sales_type) === 1;
    setIsProjectHoursMode(isProjectTask);

    // Open the form first
    setshowtask(true);

    // Small delay to let form fields mount
    setTimeout(async () => {
      try {
        // 1. Handle Project Planner data (Track, Module, Task Name, etc.)
        if (!isProjectTask && (Tasks.Track_ID || Tasks.Module_ID)) {
          setIsProjectPlanner(true);

          // Fetch Tracks if not loaded
          if (trackList.length === 0) {
            await fetchTracks();
          }

          // Fetch Modules for this Track
          if (Tasks.Track_ID) {
            await fetchModules(Tasks.Track_ID, true); // true = edit mode (don't clear form)
          }

          // Fetch Task List for this Track + Module
          if (Tasks.Track_ID && Tasks.Module_ID) {
            await fetchTaskList(Tasks.Track_ID, Tasks.Module_ID, true);
          }
        }

        // 2. Set ALL form values AFTER data is loaded
        const assignedIds = [];
        // Check both Assigned_To and Task_Members (Task_Members is what API returns)



        const memberField = Tasks.Assigned_To || Tasks.Task_Members;
        if (memberField) {
          try {
            const parsed = typeof memberField === 'string'
              ? JSON.parse(memberField)
              : memberField;
            assignedIds.push(...(Array.isArray(parsed) ? parsed : []));
          } catch (e) {
            // Fallback for comma-separated string
            const ids = String(memberField)
              .replace('[', '').replace(']', '')
              .split(',')
              .map(id => parseInt(id.trim()))
              .filter(id => !isNaN(id));
            assignedIds.push(...ids);
          }
        }

        const filteredMembers = assignedIds.filter(id => id && id !== "All");

        form.setFieldsValue({
          // Basic fields
          Req_title: Tasks.Task_name || Tasks.Item_Name || "",
          Task_Description: Tasks.Task_Description || Tasks.Description || "",
          Billing_Status: typeof Tasks.Billing_Status === "string"
            ? (Tasks.Billing_Status.toLowerCase() === "billable" ? 1 : 0)
            : (Tasks.Billing_Status ?? 1),
          Item_Priority: parseInt(Tasks.Item_Priority || Tasks.Priority) || 3,
          Task_Status: parseInt(Tasks.Item_Status || Tasks.Status) || 1,
          Est_hours: Number(Tasks.Planned_Hours || 0),

          // Dates
          taskStartDate: Tasks.Start_data ? dayjs(Tasks.Start_data) : null,

          taskEndDate: Tasks.End_date
            ? dayjs(Tasks.End_date)
            : (form.getFieldValue("end_date") ? dayjs(form.getFieldValue("end_date")) : null),



          // Assign To
          Assign_To: filteredMembers,

          // Project Planner fields
          trackType: Tasks.Track_ID || null,
          moduleType: Tasks.Module_ID || null,
          Task_List_ID: Tasks.Task_List_ID || null,

          clientInvolved: Tasks.Client_Involved != null ? String(Tasks.Client_Involved) : null,
          signOffRequired: Tasks.Sign_Off_Required != null ? String(Tasks.Sign_Off_Required) : null,
        });

        // Update local state for Assign To
        setAssselectedignto(filteredMembers);

        // ── Scroll to task form section ──
        setTimeout(() => {
          const taskFormSection = document.getElementById("task-form-section");
          if (taskFormSection) {
            taskFormSection.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 50);

      } catch (error) {
        console.error("Error loading task for edit:", error);
      }
    }, 150); // 150ms delay works reliably
  };

  // ── Task List: Open/In progress vs Completed, search, sort (one place) ──
  const taskStatusOf = (t) => Number(t.Status ?? t.Item_Status);
  const isOpenTask = (t) => [1, 2].includes(taskStatusOf(t));      // Open, In Progress
  const isCompletedTask = (t) => taskStatusOf(t) === 3;             // Completed (0 = deleted → never shown)

  const taskListTasks = (tasks || []).filter((t) => Number(t.Sales_type) === 0);
  const openTaskCount = taskListTasks.filter(isOpenTask).length;
  const completedTaskCount = taskListTasks.filter(isCompletedTask).length;

  const visibleTasks = taskListTasks
    .filter((t) => (taskView === "completed" ? isCompletedTask(t) : isOpenTask(t)))
    .filter((item) => {
      if (!taskSearchText.trim()) return true;
      const query = taskSearchText.toLowerCase().trim();
      const trackName = allTrackList.find((t) => t.Track_ID === item.Track_ID)?.Track_Name || "";
      const moduleName = allModuleList.find((m) => m.Module_ID === item.Module_ID)?.Module_Name || "";
      const billingStatus = item.Billing_Status === 1 ? "billable" : "non-billable";
      return (
        (item.Task_name || "").toLowerCase().includes(query) ||
        trackName.toLowerCase().includes(query) ||
        moduleName.toLowerCase().includes(query) ||
        billingStatus.includes(query) ||
        (item.Status_Name || "").toLowerCase().includes(query) ||
        String(item.Planned_Hours || 0).includes(query) ||
        String(item.Worked_hours || 0).includes(query)
      );
    })
    .sort((a, b) => {
      const aHasPlanner = a.Track_ID ? 1 : 0;
      const bHasPlanner = b.Track_ID ? 1 : 0;
      if (bHasPlanner !== aHasPlanner) return bHasPlanner - aHasPlanner;
      if (aHasPlanner && bHasPlanner) {
        if (a.Track_ID !== b.Track_ID) return (a.Track_ID || 0) - (b.Track_ID || 0);
        if (a.Module_ID !== b.Module_ID) return (a.Module_ID || 0) - (b.Module_ID || 0);
        return (a.Task_List_ID || 0) - (b.Task_List_ID || 0);
      }
      return (a.ItemId || 0) - (b.ItemId || 0);
    });

  const visibleTaskIds = visibleTasks.map((t) => t.ItemId);
  const allVisibleSelected = visibleTaskIds.length > 0 && visibleTaskIds.every((id) => selectedTaskIds.includes(id));
  const someVisibleSelected = visibleTaskIds.some((id) => selectedTaskIds.includes(id)) && !allVisibleSelected;
  const showTrackColumn = taskListTasks.some((t) => t.Track_ID);
  const showModuleColumn = taskListTasks.some((t) => t.Module_ID);

  const switchTaskView = (view) => {
    setTaskView(view);
    setSelectedTaskIds([]);   // never carry a selection across views
  };

  // status: 3 = mark completed, 1 = reopen
  const handleBulkTaskStatus = async (status) => {
    if (selectedTaskIds.length === 0 || !projid) return;
    setBulkTaskLoading(true);
    try {
      const res = await Timesheetapi.post("api/Projects/UpdateTaskStatus", {
        empid: Number(localStorage.getItem("employee_id")) || 0,
        roleId: localStorage.getItem("emp_role_id"),
        Project_id: projid,
        Item_Ids: selectedTaskIds,
        Status: status,
      });
      if (res.data?.success) {
        toast.success(res.data.message);
        setSelectedTaskIds([]);
        await refreshModalData(projid);   // reloads tasks; the view (Open / Completed) stays as it is
      } else {
        toast.error(res.data?.message || "Could not update the tasks.");
      }
    } catch (err) {
      console.error("Bulk task status failed:", err);
      toast.error("Could not update the tasks. Please try again.");
    } finally {
      setBulkTaskLoading(false);
    }
  };


  return (
    <div>
      <ToastContainer />
      {contextHolder}

      <div className="mx-auto px-0">
        <div className="mb-[5px]">
          <h2 className="page-heading relative bottom-[-10px]">PROJECTS</h2>
        </div>

        {/* ── Project Counts Section (Activity Card Style) ── */}
        <div className="mt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="text-center">
                <span className="text-[12px] font-[500] text-gray-600">Project Count</span>
                <div className="text-[28px] font-[600] text-blue-600 mt-2">
                  {countsLoading ? <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" /> : totalProjectCount}
                </div>
                <span className="text-[11px] text-gray-500 block mt-1">
                  {Array.isArray(clientfiltervalue) ? (clientfiltervalue[0] === "All" ? "All Clients" : clientfiltervalue[0]) : (clientfiltervalue === "All" ? "All Clients" : clientfiltervalue)}
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="text-center">
                <span className="text-[12px] font-[500] text-gray-600">Open Projects</span>
                <div className="text-[28px] font-[600] text-green-600 mt-2">
                  {countsLoading ? <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" /> : openProjectCount}
                </div>
                <span className="text-[11px] text-gray-500 block mt-1">Status: Open</span>
              </div>
            </div>

            <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="text-center">
                <span className="text-[12px] font-[500] text-gray-600">Closed Projects</span>
                <div className="text-[28px] font-[600] text-red-600 mt-2">
                  {countsLoading ? <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" /> : closedProjectCount}
                </div>
                <span className="text-[11px] text-gray-500 block mt-1">Status: Closed</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Top bar: Create button + filters ── */}
        <div className="flex flex-wrap justify-between items-end gap-[15px] relative top-[46px] z-[9] px-5 ">
          <div className="flex items-end gap-2 flex-1 min-w-0">
            <button
              onClick={toggleModalclick}
              disabled={localStorage.getItem("emp_role_id") === "3" ? false : true}
              style={{
                opacity: localStorage.getItem("emp_role_id") === "3" ? 1 : 0.5,
                cursor: localStorage.getItem("emp_role_id") === "3" ? "pointer" : "not-allowed",
              }}
              className="bg-blue-500 text-white py-[5px] px-3 rounded flex items-center justify-center w-[155px] h-[32px]"
            >
              <span className="mr-2">
                <i class="fa fa-plus-circle text-[16px] relative top-[1px]"></i>
              </span>
              <span
                className="text-[13px] text-nowrap"
                onClick={() => {
                  setIsModalOpen(true);
                  setshowtask(false);
                }}
              >
                Create a Project
              </span>
            </button>

            <div className="w-[150px] flex flex-col gap-[5px]">
              <label className="text-[13px] pl-2">Clients:</label>
              <Select
                value={clientfiltervalue}
                onChange={handleChange}
                options={clientfilteroptions}
                placeholder="Select a Client"
              />
            </div>

            <div className="w-[150px] flex flex-col gap-[5px]">
              <label className="text-[13px] pl-2">Status:</label>
              <Select
                defaultValue={1}
                value={selectedStatus}
                onChange={handleStatusChange}
                options={[
                  { label: "Open", value: 1 },
                  { label: "Closed", value: 3 },
                  { label: "Deleted", value: 2 },
                  { label: "All", value: "All" },
                ]}
                placeholder="Select a Status"
              />
            </div>

            <div className="flex flex-col gap-[5px]">
              <label className="text-[13px] pl-2">Time Periods:</label>
              <Select
                style={{ width: 140 }}
                placeholder="Time Period"
                value={selectedTimePeriod}
                options={[
                  { label: "Day", value: "day" },
                  { label: "Weekly Report", value: "week" },
                  { label: "Monthly Report", value: "month" },
                  { label: "Yearly Report", value: "year" },
                ]}
                onChange={handleTimePeriodChange}
              />
            </div>

            <div className="flex flex-col gap-[5px]">
              <label className="text-[13px] pl-2">Date:</label>
              {selectedTimePeriod === "month" && (
                <RangePicker
                  className="w-[260px]"
                  allowClear={false}
                  value={selectedDates}
                  defaultValue={[dayjs().startOf("month"), dayjs().endOf("month")]}
                  picker="month"
                  placeholder={["Start Month", "End Month"]}
                  format="MMMM YYYY"
                  onChange={handleDateChange}
                  disabledDate={(current) => current && current > dayjs().endOf("month")}
                />
              )}
              {selectedTimePeriod === "year" && (
                <RangePicker
                  className="w-[200px]"
                  allowClear={false}
                  value={selectedDates}
                  defaultValue={[dayjs().startOf("year"), dayjs().endOf("year")]}
                  picker="year"
                  placeholder={["Start Year", "End Year"]}
                  format="YYYY"
                  onChange={handleDateChange}
                  disabledDate={(current) => current && current.year() > dayjs().year()}
                />
              )}
              {selectedTimePeriod === "day" && (
                <RangePicker
                  className="w-[250px]"
                  allowClear={false}
                  value={selectedDates}
                  defaultValue={[dayjs().startOf("day"), dayjs().endOf("day")]}
                  placeholder={["Start Date", "End Date"]}
                  format="DD-MM-YYYY"
                  onChange={handleDateChange}
                  disabledDate={(current) => current && current > dayjs().endOf("day")}
                />
              )}
              {selectedTimePeriod === "week" && Flatpickr && (
                <Flatpickr
                  className={`ant-select ant-select-outlined w-[180px] date-picker-client-report`}
                  value={selectedWeekDates}
                  options={{
                    mode: "range",
                    dateFormat: "D, M j",
                    onChange: handleWeekSelection,
                    disable: [
                      function (date) {
                        // Disable future dates
                        return date > new Date();
                      },
                    ],
                  }}
                  placeholder="Select Week"
                  disabled={false}
                />
              )}
            </div>

            <div className="flex flex-col gap-[5px] flex-1 min-w-[160px]">
              <label className="text-[13px]">Search Projects</label>
              <Input
                placeholder="Search by Project Name, Client, or Status..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                prefix={<i className="fa fa-search text-gray-400"></i>}
                className="h-[32px] w-full"
                onPressEnter={() => applyFilters()}   // optional: search on Enter
              />
            </div>

          </div>

          {/* NEW: Search Filter - placed right next to date filter */}
          {/* <div className="flex items-end gap-2"> */}
          {/* <div className="flex flex-col gap-[5px]">
            <label className="text-[13px]">Search Projects</label>
            <Input
              placeholder="Search by Project Name, Client, or Status..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              prefix={<i className="fa fa-search text-gray-400"></i>}
              className="h-[32px] w-[410px]"
              onPressEnter={() => applyFilters()}   // optional: search on Enter
            />
          </div> */}
          {/* <button style={{border:'1px solid #d9d9d9',borderRadius:'5px', paddingInline:'10px'}} 
          onClick={() => {
            setSearchText("");
          }}
          className="h-[32px] clear-filter"
        >
          Clear Filters
        </button> */}
          {/* </div> */}



        </div>

        {/* ── Project Create / Edit Modal ── */}
        {isModalOpen && localStorage.getItem("emp_role_id") == 3 && (
          <AntdModal
            className="workitem-popup task-popup project-popup"
            visible={isModalOpen}
            footer={false}
            onCancel={() => setIsModalOpen(false)}
            closable
            maskClosable={false}
            keyboard={false}
          >
            <div className="bg-white rounded-lg">
              <h3
                className="text-[14px] font-[600] mb-4 px-[22px] mx-[-22px]"
                style={{ borderBottom: "1px solid #ddd", paddingBottom: "15px" }}
              >
                {edit ? "Update a Project" : "Create a Project"}
              </h3>

              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <div className="task-project-modal">

                  {/* ── Project-level fields ── */}
                  <div className="grid grid-cols-5 project-fields-grid">
                    <Form.Item
                      label="Client"
                      name="Client"
                      rules={[{ required: true, message: "Please select a client" }]}
                    >
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[200px]" />
                      ) : (
                        <Select
                          showSearch
                          allowClear
                          value={selectedClient}
                          onChange={(e) => setSelectedClient(e)}
                          optionFilterProp="label"
                          placeholder="Select client"
                          options={client}
                          className="h-10"
                          disabled={edit}
                        />
                      )}
                    </Form.Item>

                    <Form.Item
                      label="Project"
                      name="Project"
                      rules={[{ required: true, message: "Please enter the Project Name" },
                        // {
                        //   pattern: /^[A-Za-z\s]+$/,
                        //   message: "Only alphabetic characters are allowed",
                        // },
                      ]}
                    >
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[200px]" />
                      ) : (
                        <Input className="h-10" placeholder="Enter project name" />
                      )}
                    </Form.Item>

                    <Form.Item
                      name="start_date"
                      label="Start date"
                      rules={[{ required: true, message: "Please select a start date" }]}
                    >
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[200px]" />
                      ) : (
                        <DatePicker className="w-full h-10" format="DD-MM-YYYY" placeholder="Select start date" />
                      )}
                    </Form.Item>

                    <Form.Item
                      name="end_date"
                      label="End date"
                      rules={[{ required: true, message: "Please select an end date" }]}
                    >
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[200px]" />
                      ) : (
                        <DatePicker
                          className="w-full h-10"
                          format="DD-MM-YYYY"
                          placeholder="Select end Date"
                          disabledDate={(current) => {
                            const startDate = form.getFieldValue('start_date');
                            if (!startDate) return false;
                            return current && current.isBefore(startDate, 'day');
                          }}
                        />
                      )}
                    </Form.Item>

                    <Form.Item
                      label="Status"
                      name="status"
                      initialValue={1}
                      rules={[{ required: true, message: "Please select a project status" }]}
                    >
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[200px]" />
                      ) : (
                        <Select className="h-10" placeholder="Select project status" allowClear>
                          <Select.Option value={1}>Open</Select.Option>
                          <Select.Option value={3}>Closed</Select.Option>
                          <Select.Option value={2} disabled>Deleted</Select.Option>
                        </Select>
                      )}
                    </Form.Item>

                    <Form.Item label="Team Lead" name="Team_Lead">
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[200px]" />
                      ) : (
                        <Select
                          className="h-10"
                          placeholder="Please select"
                          showSearch
                          optionFilterProp="label"
                          allowClear
                          value={TeamLead}
                          onChange={(e) => setTeamLead(e)}
                          options={Employee}
                        />
                      )}
                    </Form.Item>

                    <Form.Item
                      label="Members"
                      name="Members"
                      rules={[{ required: true, message: "Please select the project members" }]}
                    >
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[200px]" />
                      ) : (



                        // <Select
                        //   placeholder="Select project members"
                        //   className="h-10"
                        //   optionFilterProp="label"
                        //   mode="multiple"
                        //   showSearch
                        //   allowClear
                        //   value={selectedAssignedToMembers}
                        //   onChange={(e) => setSelectedAssignedToMembers(e)}


                        //   //options={Employee}
                        //   options={[
                        //     ...(Employee || [])
                        //       .filter((emp) =>
                        //         selectedAssignedToMembers.map(Number).includes(Number(emp.value))
                        //       )
                        //       .sort((a, b) => a.label.localeCompare(b.label)),
                        //     ...(Employee || [])
                        //       .filter(
                        //         (emp) =>
                        //           !selectedAssignedToMembers.map(Number).includes(Number(emp.value))
                        //       )
                        //       .sort((a, b) => a.label.localeCompare(b.label)),
                        //   ]}


                        //   maxTagCount={0}
                        //   maxTagPlaceholder={(selectedValues) => {
                        //     // ✅ Use actual selected values from antd, filter out invalid ones
                        //     const validSelected = selectedValues.filter((v) => {
                        //       const emp = Employee?.find(
                        //         (e) => Number(e.value) === Number(v.value)
                        //       );
                        //       return !!emp; // only count employees that exist in Employee list
                        //     });
                        //     const count = validSelected.length;
                        //     return `${count} Employee${count === 1 ? "" : "s"}`;
                        //   }}
                        // />

                        <Select
                          placeholder="Select project members"
                          className="h-10"
                          optionFilterProp="label"
                          mode="multiple"
                          showSearch
                          allowClear
                          value={selectedAssignedToMembers}
                          onChange={(newVal) => {
                            if (newVal.includes("All")) {
                              const allIds = (Employee || []).map((e) => e.value);
                              setSelectedAssignedToMembers(allIds);
                              form.setFieldsValue({ Members: allIds });
                              return;
                            }
                            setSelectedAssignedToMembers(newVal);
                            form.setFieldsValue({ Members: newVal });
                          }}
                          options={[
                            { value: "All", label: "All" },
                            ...(Employee || [])
                              .filter((emp) => selectedAssignedToMembers.map(Number).includes(Number(emp.value)))
                              .sort((a, b) => a.label.localeCompare(b.label)),
                            ...(Employee || [])
                              .filter((emp) => !selectedAssignedToMembers.map(Number).includes(Number(emp.value)))
                              .sort((a, b) => a.label.localeCompare(b.label)),
                          ]}
                          maxTagCount={0}
                          maxTagPlaceholder={(selectedValues) => {
                            const validSelected = selectedValues.filter((v) => {
                              const emp = Employee?.find((e) => Number(e.value) === Number(v.value));
                              return !!emp;
                            });
                            const count = validSelected.length;
                            return `${count} Employee${count === 1 ? "" : "s"}`;
                          }}
                        />




                      )}
                    </Form.Item>

                    <Form.Item
                      label="Project Type"
                      name="Project_type"
                      initialValue={1}
                      rules={[{ required: true, message: "Please select a project type" }]}
                    >
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[200px]" />
                      ) : (
                        <Select
                          className="h-10"
                          placeholder="Select project type"
                          allowClear
                          onChange={(val) => setCurrentProjectType(val)}
                        >
                          <Select.Option value={1}>OPS</Select.Option>
                          <Select.Option value={2}>Onetime</Select.Option>
                          <Select.Option value={3}>Annual</Select.Option>
                        </Select>
                      )}
                    </Form.Item>

                    <Form.Item
                      label="Project Hours"
                      name="Project_hours"
                      rules={[{ required: true, message: "Please enter the project hours" }]}
                    >
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[100px]" />
                      ) : (
                        <InputNumber className="w-full h-10" placeholder="Enter project hours" min={0} />
                      )}
                    </Form.Item>

                    <Form.Item label="Balance Hours" name="Balance_hours">
                      {projectload ? (
                        <Skeleton active paragraph={false} className="w-[100px]" />
                      ) : (
                        <InputNumber
                          className="w-full h-10"
                          placeholder="Project balance hours"
                          min={0}
                          disabled
                        />
                      )}
                    </Form.Item>




                  </div>
                  <Form.Item name="Project_description" style={{ marginBottom: 0 }}>
                    {projectload ? (
                      <Skeleton active paragraph={true} className="w-full" />
                    ) : (
                      <TextArea
                        className=""
                        placeholder="Description about project"
                        rows={1}
                        style={{ resize: "vertical" }}
                      />
                    )}
                  </Form.Item>

                  {/* ── Add Sales / Create Task buttons — always visible, regardless of edit mode or existing tasks ── */}
                  <div className="flex h-fit justify-between self-center mt-4">
                    <h2 className="text-[14px] font-[600] mb-3">Added Sales</h2>
                    <div className="flex items-center gap-3 justify-end">
                      {/* Project Planner checkbox — only for Freshwork clients */}


                      {/* {client.find(c => c.value === form.getFieldValue('Client'))?.label?.toLowerCase().includes("freshwork")


                      &&


                      (
                        <Form.Item
                          name="Project_Planner"
                          valuePropName="checked"
                          initialValue={false}
                          style={{ marginBottom: 0 }}
                        >
                          <Checkbox
                            style={{ fontSize: 13 }}
                            onChange={(e) => {
                              setIsProjectPlanner(e.target.checked);
                              if (e.target.checked) {
                                fetchTracks();
                                fetchAllTaskList();
                              } else {
                                setTrackList([]); setModuleList([]); setTaskListOptions([]); setTaskListPreview([]); setTaskListPreviewLoading(false);
                                setPreviewSelectedTrackIds([]);
                                setPreviewSelectedModuleId(null);
                                setSelectedTaskListItems([]); // Reset selected tasks
                                form.setFieldsValue({ trackType: null, moduleType: null, Task_List_ID: null, clientInvolved: null, signOffRequired: null });
                              }
                            }}
                          >Project Planner</Checkbox>
                        </Form.Item>
                      )} */}


                      {(() => {
                        const clientLabel = client.find(c => c.value === form.getFieldValue('Client'))?.label || "";
                        const planner = getPlannerType(clientLabel);
                        return planner ? (
                          <Form.Item
                            name="Project_Planner"
                            valuePropName="checked"
                            initialValue={false}
                            style={{ marginBottom: 0 }}
                          >
                            <Checkbox
                              style={{ fontSize: 13 }}
                              onChange={(e) => {
                                setIsProjectPlanner(e.target.checked);
                                if (e.target.checked) {
                                  fetchTracks(planner.type);        // ← pass planner type
                                  fetchAllTaskList(planner.type);   // ← pass planner type
                                } else {
                                  setTrackList([]); setModuleList([]);
                                  setTaskListOptions([]); setTaskListPreview([]);
                                  setTaskListPreviewLoading(false);
                                  setPreviewSelectedTrackIds([]);
                                  setPreviewSelectedModuleId(null);
                                  setSelectedTaskListItems([]);
                                  form.setFieldsValue({
                                    trackType: null, moduleType: null,
                                    Task_List_ID: null, clientInvolved: null,
                                    signOffRequired: null
                                  });
                                }
                              }}
                            >{planner.label}</Checkbox>
                          </Form.Item>
                        ) : null;
                      })()}




                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setIsProjectHoursMode(true);
                          setshowtask(true);
                          settaskId(0);
                          form.setFieldsValue({
                            salesType: null,
                            taskStartDate: null,
                            taskEndDate: null,
                            Task_Status: 1,
                            Est_hours: null,
                            Assign_To: [],
                          });
                        }}
                        className="flex items-center text-[13px] hover:text-blue-600 create-btn"
                        style={{ cursor: "pointer", position: 'relative', zIndex: '99' }}
                      >
                        <i className="fa fa-plus-circle text-[16px] mr-2"></i>
                        {/* <span>Add Project Hours</span> */}
                        <span>Add Sales</span>
                      </button>
                      {/* <button
                        onClick={(e) => {
                          e.preventDefault();
                          setIsProjectHoursMode(false);
                          setshowtask(true);
                          settaskId(0);
                          form.setFieldsValue({
                            Task_Status: 1,
                            Item_Priority: 3,
                            Assign_To: [],
                            Req_title: null,
                            Task_Description: null,
                            Billing_Status: 1,
                          });
                        }}
                        className="flex items-center text-[13px] hover:text-blue-600 create-btn"
                        style={{ cursor: "pointer" }}
                      >
                        <i className="fa fa-plus-circle text-[16px] mr-2"></i>
                        <span>Create a Task</span>
                      </button> */}
                      <button style={{ position: 'relative', zIndex: '99' }}
                        onClick={(e) => {
                          e.preventDefault();
                          setIsProjectHoursMode(false);
                          setIsProjectPlanner(false);
                          setshowtask(true);
                          settaskId(0);
                          setSelectedTaskListItems([]); // Reset selected tasks for new task form


                          // Only reset task-related fields, preserve project details
                          form.setFieldsValue({
                            Task_Status: 1,
                            Item_Priority: 3,
                            Assign_To: [],
                            Req_title: "",
                            Task_Description: "",
                            Billing_Status: 1,
                            trackType: null,
                            moduleType: null,
                            Task_List_ID: null,
                            clientInvolved: null,
                            signOffRequired: null,
                            taskStartDate: form.getFieldValue("start_date"),
                            taskEndDate: form.getFieldValue("end_date"),
                            Est_hours: null,
                            Assign_To: form.getFieldValue("Members"),
                          });

                          setAssselectedignto([]);
                        }}
                        className="flex items-center text-[13px] hover:text-blue-600 create-btn"
                      >
                        <i className="fa fa-plus-circle text-[16px] mr-2"></i>
                        <span>Create a Task</span>
                      </button>

                    </div>
                  </div>

                  {/* ── Added Sales table (edit mode only) ── */}
                  {edit && (
                    <Spin spinning={tablesLoading} tip="Updating...">
                      <div>

                        {/* Added Project List — shows Add Project Hours tasks */}
                        {tasks && tasks.length > 0 && (
                          (() => {
                            // Only show tasks with status != 0
                            // const projTasks = tasks.filter((t) => Number(t.Sales_type) === 1 && Number(t.Status) !== 1);
                            const projTasks = tasks.filter((t) => Number(t.Sales_type) === 1);
                            if (projTasks.length === 0) return null;
                            return (
                              <div className="mt-4">
                                <table className="w-full border-collapse">
                                  <thead>
                                    <tr className="bg-[#f1f1f1]">
                                      <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Sales No</th>
                                      <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Sale Type</th>
                                      <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Status</th>
                                      <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Start Date</th>
                                      <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">End Date</th>
                                      <th className="border px-2 py-1.5 text-center text-[13px] font-[500]">Estimated Hours</th>
                                      <th className="border px-2 py-1.5 text-center text-[13px] font-[500]">Members</th>
                                      <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {projTasks.map((task, idx) => {
                                      const estimated = Number(task.Planned_Hours || 0);
                                      const worked = Number(task.Worked_hours || 0);
                                      const balance = Math.max(0, estimated - worked);
                                      return (
                                        <tr key={idx} className="hover:bg-gray-50">
                                          <td className="border px-2 py-1 text-[12px] font-medium text-blue-700">
                                            {task.Sales_No || "-"}
                                          </td>
                                          <td className="border px-2 py-1 text-[12px]">{task.Task_name}</td>
                                          <td className="border px-2 py-1 text-[12px]">{task.Status_Name}</td>
                                          <td className="border px-2 py-1 text-[12px]">
                                            {task.Start_data ? dayjs(task.Start_data).format("DD-MM-YYYY") : "-"}
                                          </td>
                                          <td className="border px-2 py-1 text-[12px]">
                                            {task.End_date ? dayjs(task.End_date).format("DD-MM-YYYY") : "-"}
                                          </td>
                                          <td className="border px-2 py-1 text-[12px] text-center">{estimated.toFixed(2)}</td>
                                          <td className="border px-2 py-1 text-[12px] text-center">
                                            {(() => {
                                              const raw = task.Task_Members || task.Assigned_To;
                                              let memberIds = [];
                                              if (raw) {
                                                try {
                                                  const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                                                  memberIds = Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
                                                } catch {
                                                  memberIds = String(raw).replace(/[\[\]]/g, "").split(",").map((id) => parseInt(id.trim())).filter(Boolean);
                                                }
                                              }
                                              const count = memberIds.filter((id) => Employee?.some((e) => Number(e.value) === Number(id))).length;
                                              return count > 0 ? count : "-";
                                            })()}
                                          </td>
                                          <td className="border px-2 py-1 text-[12px]">
                                            <div className="flex gap-3">
                                              <div
                                                className="text-blue-600 hover:underline cursor-pointer"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleEditTask(task.ItemId);
                                                }}
                                              >
                                                <FaRegEdit className="text-[16px]" />
                                              </div>
                                              <div
                                                className="text-red-600 hover:underline cursor-pointer"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  toggleTaskDelete(task);
                                                }}
                                              >
                                                <FaTrashAlt className="text-[15px]" />
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()
                        )}
                      </div>
                    </Spin>
                  )}

                  {/* ── Project Planner Task List Preview — shown as soon as checkbox is checked ── */}
                  {isProjectPlanner && taskListPreviewLoading && (
                    <div className="mt-5 rounded-[8px] border border-[#3b82f6] overflow-hidden">
                      <div className="px-3 py-2 bg-[#e8f4fd] flex items-center justify-between">
                        <h2 className="text-[14px] font-[600]" style={{ margin: 0 }}>
                          {(() => {
                            const clientLabel = client.find(c => c.value === form.getFieldValue('Client'))?.label || "";
                            const planner = getPlannerType(clientLabel);
                            return planner?.label || "Project Planner";
                          })()} — Task List (loading...)
                        </h2>
                      </div>
                      <div style={{ maxHeight: 480, overflowY: "auto" }}>
                        <Skeleton active paragraph={{ rows: 8 }} />
                      </div>
                    </div>
                  )}

                  {isProjectPlanner && !taskListPreviewLoading && taskListPreview.length === 0 && (
                    <div className="mt-5 rounded-[8px] border border-[#ff4d4f] p-3 bg-[#fff2f0]">
                      <p className="text-[#ff4d4f] text-[13px] font-[500]">Project Planner list is missing</p>
                    </div>
                  )}

                  {isProjectPlanner && !taskListPreviewLoading && taskListPreview.length > 0 && (
                    <div className="mt-5 rounded-[8px] border border-[#3b82f6] overflow-hidden">
                      <div className="px-3 py-2 bg-[#e8f4fd]">
                        <div className="flex items-center justify-between mb-3">
                          <h2 className="text-[14px] font-[600]" style={{ margin: 0 }}>
                            {(() => {
                              const clientLabel = client.find(c => c.value === form.getFieldValue('Client'))?.label || "";
                              const planner = getPlannerType(clientLabel);
                              return planner?.label || "Project Planner";
                            })()} — Task List  ({
                              (() => {
                                let filtered = taskListPreview;
                                if (previewSelectedTrackIds.length > 0) {
                                  filtered = filtered.filter(t => previewSelectedTrackIds.includes(t.Track_ID));
                                }
                                if (previewSelectedModuleId) {
                                  filtered = filtered.filter(t => t.Module_ID === previewSelectedModuleId);
                                }
                                return filtered.length;
                              })()
                            } tasks
                            {selectedTaskListItems.length > 0 && (
                              <span style={{ color: '#52c41a', marginLeft: '8px' }}>
                                — {selectedTaskListItems.length} selected
                              </span>
                            )}
                            )
                          </h2>
                        </div>
                        <div className="flex gap-3">
                          <Select
                            mode="multiple"
                            placeholder="Select Track(s)"
                            style={{ width: 250 }}
                            allowClear


                            // onChange={(values) => {
                            //   setPreviewSelectedTrackIds(values);
                            //   setPreviewSelectedModuleId(null);
                            // }}

                            onChange={(values) => {
                              setPreviewSelectedTrackIds(values);
                              setPreviewSelectedModuleId(null);

                              if (values && values.length > 0) {
                                const tasksForSelectedTracks = taskListPreview.filter(task =>
                                  values.includes(task.Track_ID)
                                );
                                const otherSelectedTasks = selectedTaskListItems.filter(s =>
                                  !values.includes(s.Track_ID)
                                );
                                setSelectedTaskListItems([...otherSelectedTasks, ...tasksForSelectedTracks]);
                              } else {
                                setSelectedTaskListItems([]);
                              }
                            }}


                            value={previewSelectedTrackIds}
                            options={
                              allTrackList.map(track => ({
                                value: track.Track_ID,
                                label: track.Track_Name
                              }))
                            }
                          />
                          {/* <Select
                            placeholder="Filter by Module"
                            style={{ width: 200 }}
                            allowClear
                            disabled={previewSelectedTrackIds.length === 0}
                            onChange={(value) => setPreviewSelectedModuleId(value)}
                            value={previewSelectedModuleId}
                            options={
                              previewSelectedTrackIds.length > 0
                                ? allModuleList
                                    .filter(m => previewSelectedTrackIds.includes(m.Track_ID))
                                    .map(module => ({
                                      value: module.Module_ID,
                                      label: module.Module_Name
                                    }))
                                : []
                            }
                          /> */}
                        </div>
                      </div>
                      <div style={{ maxHeight: 480, overflowY: "auto" }}>
                        <table className="w-full border-collapse">
                          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr className="bg-[#f0f6ff]">
                              <th className="border px-2 py-1.5 text-left text-[12px] font-[600]" style={{ width: 40 }}>
                                <Checkbox
                                  checked={
                                    (() => {
                                      const filtered = taskListPreview
                                        .filter(task => previewSelectedTrackIds.length === 0 || previewSelectedTrackIds.includes(task.Track_ID))
                                        .filter(task => !previewSelectedModuleId || task.Module_ID === previewSelectedModuleId);
                                      return filtered.length > 0 && filtered.every(t => selectedTaskListItems.some(s => s.Task_List_ID === t.Task_List_ID));
                                    })()
                                  }
                                  indeterminate={
                                    (() => {
                                      const filtered = taskListPreview
                                        .filter(task => previewSelectedTrackIds.length === 0 || previewSelectedTrackIds.includes(task.Track_ID))
                                        .filter(task => !previewSelectedModuleId || task.Module_ID === previewSelectedModuleId);
                                      const selected = filtered.filter(t => selectedTaskListItems.some(s => s.Task_List_ID === t.Task_List_ID));
                                      return selected.length > 0 && selected.length < filtered.length;
                                    })()
                                  }
                                  onChange={(e) => {
                                    const filtered = taskListPreview
                                      .filter(task => previewSelectedTrackIds.length === 0 || previewSelectedTrackIds.includes(task.Track_ID))
                                      .filter(task => !previewSelectedModuleId || task.Module_ID === previewSelectedModuleId);
                                    if (e.target.checked) {
                                      setSelectedTaskListItems([...selectedTaskListItems, ...filtered.filter(t => !selectedTaskListItems.some(s => s.Task_List_ID === t.Task_List_ID))]);
                                    } else {
                                      setSelectedTaskListItems(selectedTaskListItems.filter(s => !filtered.some(f => f.Task_List_ID === s.Task_List_ID)));
                                    }
                                  }}
                                />
                              </th>
                              <th className="border px-2 py-1.5 text-left text-[12px] font-[600]">#</th>
                              <th className="border px-2 py-1.5 text-left text-[12px] font-[600]">Track</th>
                              <th className="border px-2 py-1.5 text-left text-[12px] font-[600]">Module</th>
                              <th className="border px-2 py-1.5 text-left text-[12px] font-[600]">Task Name</th>
                              <th className="border px-2 py-1.5 text-center text-[12px] font-[600]">Client Involved</th>
                              <th className="border px-2 py-1.5 text-center text-[12px] font-[600]">Sign-Off Required</th>
                            </tr>
                          </thead>
                          <tbody>
                            {taskListPreview
                              .filter(task => previewSelectedTrackIds.length === 0 || previewSelectedTrackIds.includes(task.Track_ID))
                              .filter(task => !previewSelectedModuleId || task.Module_ID === previewSelectedModuleId)
                              .sort((a, b) => {
                                if (a.Track_ID !== b.Track_ID) {
                                  return a.Track_ID - b.Track_ID;
                                }
                                return a.Module_ID - b.Module_ID;
                              })
                              .map((task, index) => (
                                <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-[#fafcff]"}>
                                  <td className="border px-2 py-1 text-[12px] text-center">
                                    <Checkbox
                                      checked={selectedTaskListItems.some(s => s.Task_List_ID === task.Task_List_ID)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedTaskListItems([...selectedTaskListItems, task]);
                                        } else {
                                          setSelectedTaskListItems(selectedTaskListItems.filter(s => s.Task_List_ID !== task.Task_List_ID));
                                        }
                                      }}
                                    />
                                  </td>
                                  <td className="border px-2 py-1 text-[12px] text-gray-400">{index + 1}</td>
                                  <td className="border px-2 py-1 text-[12px]">
                                    {allTrackList.find(t => t.Track_ID === task.Track_ID)?.Track_Name || "-"}
                                  </td>
                                  <td className="border px-2 py-1 text-[12px]">
                                    {allModuleList.find(m => m.Module_ID === task.Module_ID)?.Module_Name || "-"}
                                  </td>
                                  <td className="border px-2 py-1 text-[12px] font-[500]">{task.Task_List_Name}</td>
                                  <td className="border px-2 py-1 text-[12px] text-center">
                                    <span className={task.Client_Involved === 1 ? "text-green-600 font-[500]" : "text-gray-400"}>
                                      {task.Client_Involved === 1 ? "Yes" : "No"}
                                    </span>
                                  </td>
                                  <td className="border px-2 py-1 text-[12px] text-center">
                                    <span className={task.Sign_Off_Required === 1 ? "text-orange-500 font-[500]" : "text-gray-400"}>
                                      {task.Sign_Off_Required === 1 ? "Yes" : "No"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── Task / Project Hours sub-form ── */}
                  {showtask && (
                    <>
                      <div id="task-form-section" className="rounded-[8px] border border-[#ddd] mt-5">
                        <div
                          className="px-3 py-2.5 bg-[#f1f1f1]"
                          style={{ borderBottom: "1px solid #ddd", borderRadius: "8px 8px 0px 0px" }}
                        >
                          <h2 className="text-[14px] font-[600] my-4" style={{ margin: 0 }}>
                            {/* {isProjectHoursMode ? "Add Project Hours" : "Create a Task"} */}
                            {isProjectHoursMode ? "Add a Sales" : "Create a Task"}

                          </h2>
                        </div>

                        <div className="px-3 py-3">
                          <div className="grid grid-cols-5 gap-x-4 gap-y-4">

                            {/* ── Create Task only fields ── */}
                            {!isProjectHoursMode && (
                              <>
                                <Form.Item
                                  label="Task Status"
                                  name="Task_Status"
                                  rules={[{ required: true, message: "Please select a task status" }]}
                                >
                                  {projectload ? (
                                    <Skeleton active paragraph={false} className="w-[200px]" />
                                  ) : (
                                    <Select
                                      allowClear
                                      placeholder="Select task status"
                                      className="h-10"
                                      defaultValue={1}
                                      options={[
                                        { value: 1, label: "Open" },
                                        { value: 2, label: "In Progress" },
                                        { value: 3, label: "Completed" },
                                      ]}
                                    />
                                  )}
                                </Form.Item>

                                <Form.Item
                                  label="Task Priority"
                                  name="Item_Priority"
                                  rules={[{ required: true, message: "Please select a task Priority" }]}
                                >
                                  {projectload ? (
                                    <Skeleton active paragraph={false} className="w-[200px]" />
                                  ) : (
                                    <Select
                                      allowClear
                                      placeholder="Select task priority"
                                      defaultValue={3}
                                      options={[
                                        { value: 1, label: "Low" },
                                        { value: 2, label: "Medium" },
                                        { value: 3, label: "High" },
                                        { value: 4, label: "Critical" },
                                      ]}
                                      className="h-10"
                                      onChange={(selectedOption) => console.log(selectedOption)}
                                    />
                                  )}
                                </Form.Item>
                              </>
                            )}

                            {/* ── Add Project Hours only fields ── */}
                            {isProjectHoursMode && (
                              <>
                                <Form.Item
                                  // label="Process Type"
                                  label="Sales Type"
                                  name="salesType"
                                  // rules={[{ required: true, message: "Please select process type" }]}
                                  rules={[{ required: true, message: "Please select sales type" }]}
                                >
                                  {projectload ? (
                                    <Skeleton active paragraph={false} className="w-[200px]" />
                                  ) : (
                                    <Select
                                      allowClear
                                      // placeholder="Select process type"
                                      placeholder="Select sales type"
                                      className="h-10"
                                      options={[
                                        { value: 1, label: "Change request" },
                                        { value: 2, label: "SOW" },
                                      ]}
                                    />
                                  )}
                                </Form.Item>
                              </>
                            )}

                            {/* Start Date — All tasks (required only for Add Project Hours) */}


                            <Form.Item
                              label="Start Date"
                              name="taskStartDate"
                              rules={[{ required: isProjectHoursMode, message: "Please select start date" }]}
                            >

                              {console.log("task_Id Prabu checking:", task_Id)}

                              {projectload ? (
                                <Skeleton active paragraph={false} className="w-[200px]" />
                              ) : (
                                <DatePicker
                                  className="w-full h-10"
                                  format="DD-MM-YYYY"
                                  placeholder="Select start date"
                                  disabledDate={(current) => {
                                    if (task_Id === 0) {
                                      const projectStartDate = form.getFieldValue("start_date");

                                      if (projectStartDate) {
                                        return current && current.isBefore(dayjs(projectStartDate), "day");
                                      }
                                    }
                                    return false;
                                  }}
                                />
                              )}
                            </Form.Item>




                            {/* End Date — All tasks (required only for Add Project Hours) */}
                            <Form.Item
                              label="End Date"
                              name="taskEndDate"
                              rules={[{ required: isProjectHoursMode, message: "Please select end date" }]}
                            >
                              {projectload ? (
                                <Skeleton active paragraph={false} className="w-[200px]" />
                              ) : (
                                <DatePicker
                                  className="w-full h-10"
                                  format="DD-MM-YYYY"
                                  placeholder="Select end date"
                                  disabledDate={(current) => {
                                    if (task_Id === 0) {
                                      const projectStartDate = form.getFieldValue("start_date");
                                      const taskStartDate = form.getFieldValue("taskStartDate");
                                      if (taskStartDate) {
                                        return current && current.isBefore(dayjs(taskStartDate), "day");
                                      }
                                      if (projectStartDate) {
                                        return current && current.isBefore(dayjs(projectStartDate), "day");
                                      }
                                    } else {
                                      // Editing existing task
                                      const taskStartDate = form.getFieldValue("taskStartDate");
                                      if (taskStartDate) {
                                        return current && current.isBefore(dayjs(taskStartDate), "day");
                                      }
                                    }
                                    return false;
                                  }}
                                />
                              )}
                            </Form.Item>

                            {/* ── Assign To — shared by both forms ── */}
                            <Form.Item
                              label={isProjectHoursMode ? "Sales Person" : "Assign To"}
                              name="Assign_To"
                              rules={[{ required: true, message: "Please select the task members" }]}
                            >
                              {projectload ? (
                                <Skeleton active paragraph={false} className="w-[200px]" />
                              ) : (


                                <Select
                                  allowClear
                                  placeholder={isProjectHoursMode ? "Select sales person" : "Select task members"}
                                  className="h-10"
                                  optionFilterProp="label"
                                  mode="multiple"
                                  showSearch
                                  onChange={(value) => {
                                    if (value.includes("All")) {
                                      const allIds = assignto.map((item) => item.value);
                                      setAssselectedignto(allIds);
                                      form.setFieldsValue({ Assign_To: allIds });
                                    } else {
                                      const allIds = assignto
                                        .filter((item) => item.value !== "all")
                                        .map((item) => item.value);
                                      const isAllSelected = allIds.every((id) =>
                                        selectedassignto.includes(id)
                                      );
                                      if (isAllSelected) {
                                        form.setFieldsValue({ Assign_To: [] });
                                        setAssselectedignto([]);
                                      } else {
                                        setAssselectedignto(value);
                                        form.setFieldsValue({ Assign_To: value });
                                      }
                                    }
                                  }}


                                  //options={assignto}


                                  options={[
                                    // "All" always on top
                                    { label: "All", value: "All" },
                                    // Selected members first (A-Z)
                                    ...(assignto || [])
                                      .filter(
                                        (emp) =>
                                          emp.value !== "All" &&
                                          emp.value !== "all" &&
                                          selectedassignto.map(Number).includes(Number(emp.value))
                                      )
                                      .sort((a, b) => a.label.localeCompare(b.label)),
                                    // Unselected members after (A-Z)
                                    ...(assignto || [])
                                      .filter(
                                        (emp) =>
                                          emp.value !== "All" &&
                                          emp.value !== "all" &&
                                          !selectedassignto.map(Number).includes(Number(emp.value))
                                      )
                                      .sort((a, b) => a.label.localeCompare(b.label)),
                                  ]}


                                  // maxTagCount={0}
                                  // maxTagPlaceholder={() => {
                                  //   const count = selectedassignto.length;
                                  //   const allIds = assignto
                                  //     .filter((item) => item.value !== "all")
                                  //     .map((item) => item.value);
                                  //   const isAllSelected = allIds.every((id) =>
                                  //     selectedassignto.includes(id)
                                  //   );
                                  //   return isAllSelected
                                  //     ? "All Employees"
                                  //     : `${count} Employee${count === 1 ? "" : "s"}`;
                                  // }}

                                  maxTagCount={0}
                                  maxTagPlaceholder={(selectedValues) => {
                                    // ✅ Filter out "All" AND only count employees that exist in Employee list
                                    const actualSelected = selectedValues.filter((v) => {
                                      if (v.value === "All" || v.value === "all") return false;
                                      const emp = Employee?.find(
                                        (e) => Number(e.value) === Number(v.value)
                                      );
                                      return !!emp; // only count valid/active employees
                                    });
                                    const count = actualSelected.length;

                                    const allIds = assignto
                                      .filter((item) => item.value !== "All" && item.value !== "all")
                                      .map((item) => item.value);

                                    const isAllSelected =
                                      allIds.length > 0 &&
                                      allIds.every((id) =>
                                        actualSelected.some((v) => Number(v.value) === Number(id))
                                      );

                                    return isAllSelected
                                      ? "All Employees"
                                      : `${count} Employee${count === 1 ? "" : "s"}`;
                                  }}



                                />



                              )}
                            </Form.Item>

                            {/* ── Estimated Hours — shared, no cap for Add Project Hours ── */}
                            <Form.Item
                              label="Estimated Hours"
                              name="Est_hours"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter the estimated hours",
                                },
                                {
                                  validator: (_, value) => {
                                    // Add Project Hours: allow any value, no project hours cap
                                    if (isProjectHoursMode) {
                                      return Promise.resolve();
                                    }
                                    const projectHoursVal = form.getFieldValue("Project_hours");
                                    if (!value || !projectHoursVal) {
                                      return Promise.resolve();
                                    }
                                    if (validateEstimatedHours(value, projectHoursVal)) {
                                      return Promise.resolve();
                                    } else {
                                      const remaining = getRemainingHours(projectHoursVal);
                                      return Promise.reject(
                                        new Error(
                                          `Estimated hours cannot exceed project hours. Remaining available: ${remaining} hours`
                                        )
                                      );
                                    }
                                  },
                                },
                              ]}
                            >
                              {projectload ? (
                                <Skeleton active paragraph={false} className="w-[100px]" />
                              ) : (
                                <InputNumber
                                  className="w-full h-10"
                                  placeholder="Enter estimated hours"
                                  min={0}
                                  onChange={() => form.validateFields(["Est_hours"])}
                                />
                              )}
                            </Form.Item>


                            <Form.Item
                              label="Billing Status"
                              name="Billing_Status"
                              //initialValue={1}
                              rules={[{ required: true, message: "Please select billing status" }]}
                            >
                              {projectload ? (
                                <Skeleton active paragraph={false} className="w-[200px]" />
                              ) : (
                                <Select
                                  placeholder="Select billing status"
                                  className="h-10"
                                  options={[
                                    { value: 1, label: "Billable" },
                                    { value: 0, label: "Non-Billable" },
                                  ]}
                                />
                              )}
                            </Form.Item>


                            {/* ── Project Planner fields — shown when checkbox is checked OR when editing a task with existing planner fields ── */}
                            {(isProjectPlanner || (task_Id > 0 && (form.getFieldValue("trackType") || form.getFieldValue("moduleType")))) && !isProjectHoursMode && (
                              <>
                                <Form.Item label="Track" name="trackType" rules={[{ required: showtask, message: "Please select a track" }]}>
                                  {projectload ? (
                                    <Skeleton active paragraph={false} className="w-[200px]" />
                                  ) : (
                                    <Select
                                      allowClear
                                      placeholder="Select track"
                                      className="h-10"
                                      options={trackList}
                                      onChange={(val) => { if (val) fetchModules(val); }}
                                    />
                                  )}
                                </Form.Item>

                                <Form.Item label="Module" name="moduleType" rules={[{ required: showtask, message: "Please select a module" }]}>
                                  {projectload ? (
                                    <Skeleton active paragraph={false} className="w-[200px]" />
                                  ) : (
                                    <Select
                                      allowClear
                                      placeholder="Select module"
                                      className="h-10"
                                      options={moduleList}
                                      disabled={moduleList.length === 0}
                                      onChange={(val) => {
                                        const trackId = form.getFieldValue("trackType");
                                        if (val && trackId) fetchTaskList(trackId, val);
                                      }}
                                    />
                                  )}
                                </Form.Item>

                                <Form.Item label="Task Name" name="Task_List_ID" hidden rules={[{ required: false }]}>
                                  {projectload ? (
                                    <Skeleton active paragraph={false} className="w-[200px]" />
                                  ) : (
                                    <Select
                                      allowClear
                                      placeholder="Select task"
                                      className="h-10"
                                      options={taskListOptions.map(t => ({ value: t.Task_List_ID, label: t.Task_List_Name }))}
                                      disabled={taskListOptions.length === 0}
                                      onChange={handleTaskListSelect}
                                    />
                                  )}
                                </Form.Item>

                                <Form.Item label="Client Involved" name="clientInvolved">
                                  {projectload ? (
                                    <Skeleton active paragraph={false} className="w-[200px]" />
                                  ) : (
                                    <Select allowClear placeholder="Client involved" className="h-10"
                                      options={[{ value: "1", label: "Yes" }, { value: "2", label: "No" }]}
                                    />
                                  )}
                                </Form.Item>

                                <Form.Item label="Sign-Off Required" name="signOffRequired">
                                  {projectload ? (
                                    <Skeleton active paragraph={false} className="w-[200px]" />
                                  ) : (
                                    <Select allowClear placeholder="Sign-off required" className="h-10"
                                      options={[{ value: "1", label: "Yes" }, { value: "2", label: "No" }]}
                                    />
                                  )}
                                </Form.Item>
                              </>
                            )}


                            <Form.Item
                              label="Task"
                              name="Req_title"
                              rules={[{ required: true, message: "Please enter the task title" }]}
                              className="col-span-2"
                            >
                              {projectload ? (
                                <Skeleton active paragraph={false} className="w-[200px]" />
                              ) : (
                                <TextArea
                                  placeholder="Enter task title"
                                  className="pt-2"
                                  style={{ height: "40px" }}
                                />
                              )}
                            </Form.Item>

                            {projectload ? (
                              <Skeleton active paragraph={false} className="w-[200px]" />
                            ) : (
                              !isProjectHoursMode && (
                                <div className="text-[12px] text-gray-600 mb-4 ml-0 mt-[13%]">
                                  {tasks && tasks.length > 0 && form.getFieldValue("Project_hours")
                                    ? `Project Hours: ${form.getFieldValue("Project_hours")} | Current Tasks Total: ${calculateTotalTaskHours(tasks).toFixed(2)} | Remaining: ${getRemainingHours(form.getFieldValue("Project_hours")).toFixed(2)}`
                                    : form.getFieldValue("Project_hours")
                                      ? `Project Hours: ${form.getFieldValue("Project_hours")} | Remaining: ${form.getFieldValue("Project_hours")}`
                                      : ""}
                                </div>
                              )
                            )}
                          </div>

                          {/* ── Task title — Create Task only ── */}
                          {!isProjectHoursMode && (
                            <div className="grid grid-cols-1 gap-4">


                              <Form.Item
                                label="Task Description"
                                name="Task_Description"
                              >
                                {projectload ? (
                                  <Skeleton active paragraph={false} className="w-[200px]" />
                                ) : (
                                  <TextArea
                                    placeholder="Enter task description"
                                    className="pt-2"
                                    style={{ height: "80px" }}
                                  />
                                )}
                              </Form.Item>


                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── Tables (edit mode only) ── */}
                  {edit && (
                    <Spin spinning={tablesLoading} tip="Updating...">
                      <div>

                        {/* Task List — shows Create Task tasks */}
                        <div className="mt-0">

                          {/* <div style={{ display: "flex", alignItems: "justify-end", gap: 10, marginBottom: 12, flexWrap: "nowrap" }} className="justify-between mt-[2.5%]">
                            <h2 className="text-[14px] font-[600]" style={{ margin: 0, whiteSpace: "nowrap", display: "block" }}>Task List</h2>
                            <Input
                              placeholder="Search tasks..."
                              value={taskSearchText}
                              onChange={(e) => setTaskSearchText(e.target.value)}
                              allowClear
                              prefix={<i className="fa fa-search text-gray-400"></i>}
                              style={{ width: 280, height: 35, fontSize: 12 }}
                            />
                          </div> */}

                          <div className="flex flex-wrap items-center justify-between gap-2.5 mb-3 mt-[2.5%]">
                            <div className="flex flex-wrap items-center gap-2">
                              <h2 className="text-[14px] font-[600] m-0 whitespace-nowrap">Task List</h2>
                              {/* antd Button = type "button", so it never submits the project form */}
                              <Button size="small" type={taskView === "open" ? "primary" : "default"} onClick={() => switchTaskView("open")}>
                                Open &amp; In progress ({openTaskCount})
                              </Button>
                              <Button size="small" type={taskView === "completed" ? "primary" : "default"} onClick={() => switchTaskView("completed")}>
                                Completed ({completedTaskCount})
                              </Button>
                              {selectedTaskIds.length > 0 && (
                                taskView === "open" ? (
                                  <Button size="small" type="primary" style={{ background: "#16a34a" }} loading={bulkTaskLoading}
                                    onClick={() => handleBulkTaskStatus(3)}>
                                    Mark as completed ({selectedTaskIds.length})
                                  </Button>
                                ) : (
                                  <Button size="small" loading={bulkTaskLoading} onClick={() => handleBulkTaskStatus(1)}>
                                    Reopen ({selectedTaskIds.length})
                                  </Button>
                                )
                              )}
                            </div>
                            <Input
                              placeholder="Search tasks..."
                              value={taskSearchText}
                              onChange={(e) => setTaskSearchText(e.target.value)}
                              allowClear
                              prefix={<i className="fa fa-search text-gray-400"></i>}
                              style={{ width: 280, height: 35, fontSize: 12 }}
                            />
                          </div>

                          <div className="custom-task-list-table">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-[#f1f1f1]">
                                  <th className="border px-2 py-1.5 text-center" style={{ width: 36 }}>
                                    <Checkbox
                                      checked={allVisibleSelected}
                                      indeterminate={someVisibleSelected}
                                      disabled={visibleTaskIds.length === 0}
                                      onChange={(e) =>
                                        setSelectedTaskIds(e.target.checked
                                          ? [...new Set([...selectedTaskIds, ...visibleTaskIds])]
                                          : selectedTaskIds.filter((id) => !visibleTaskIds.includes(id)))
                                      }
                                    />
                                  </th>
                                  {showTrackColumn && <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Track</th>}
                                  {showModuleColumn && <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Module</th>}
                                  <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Task ID</th>
                                  <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Task Name</th>
                                  <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Billing Status</th>
                                  <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Status</th>
                                  <th className="border px-2 py-1.5 text-center text-[13px] font-[500]">Estimated Hours</th>
                                  <th className="border px-2 py-1.5 text-center text-[13px] font-[500]">Worked Hours</th>
                                  <th className="border px-2 py-1.5 text-center text-[13px] font-[500]">Balance Hours</th>
                                  <th className="border px-2 py-1.5 text-center text-[13px] font-[500]">Members</th>
                                  <th className="border px-2 py-1.5 text-left text-[13px] font-[500]">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleTasks.length > 0 ? (
                                  visibleTasks.map((task, index) => {
                                    const allotted = Number(task.Planned_Hours || 0);
                                    const utilized = Number(task.Worked_hours || 0);
                                    const balance = Math.max(0, allotted - utilized);
                                    const checked = selectedTaskIds.includes(task.ItemId);

                                    const memberIds = (() => {
                                      const raw = task.Task_Members || task.Assigned_To;
                                      if (!raw) return [];
                                      try {
                                        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                                        return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
                                      } catch {
                                        return String(raw).replace(/[\[\]]/g, "").split(",").map((id) => parseInt(id.trim())).filter(Boolean);
                                      }
                                    })();
                                    const memberCount = memberIds.filter((id) => Employee?.some((e) => Number(e.value) === Number(id))).length;

                                    return (
                                      <tr key={task.ItemId ?? index} className={checked ? "bg-blue-50" : "hover:bg-gray-50"}>
                                        <td className="border px-2 py-1 text-center">
                                          <Checkbox
                                            checked={checked}
                                            onChange={(e) =>
                                              setSelectedTaskIds((prev) =>
                                                e.target.checked ? [...prev, task.ItemId] : prev.filter((id) => id !== task.ItemId))
                                            }
                                          />
                                        </td>
                                        {showTrackColumn && (
                                          <td className="border px-2 py-1 text-[12px]">
                                            {task.Track_ID ? (allTrackList.find((t) => t.Track_ID === task.Track_ID)?.Track_Name || "-") : "-"}
                                          </td>
                                        )}
                                        {showModuleColumn && (
                                          <td className="border px-2 py-1 text-[12px]">
                                            {task.Module_ID ? (allModuleList.find((m) => m.Module_ID === task.Module_ID)?.Module_Name || "-") : "-"}
                                          </td>
                                        )}
                                        <td className="border px-2 py-1 text-[12px]">{task.Ticket_id || task.ItemId || "-"}</td>
                                        <td className="border px-2 py-1 text-[12px]">{task.Task_name}</td>
                                        <td className="border px-2 py-1 text-[12px]">
                                          <span className={`px-2 py-1 rounded text-[11px] font-[500] ${task.Billing_Status === 1 ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}>
                                            {task.Billing_Status === 1 ? "Billable" : task.Billing_Status === 0 ? "Non-Billable" : "-"}
                                          </span>
                                        </td>
                                        <td className="border px-2 py-1 text-[12px]">{task.Status_Name}</td>
                                        <td className="border px-2 py-1 text-[12px] text-center">{allotted.toFixed(2)}</td>
                                        <td className="border px-2 py-1 text-[12px] text-center">{utilized.toFixed(2)}</td>
                                        <td className="border px-2 py-1 text-[12px] text-center">{balance.toFixed(2)}</td>
                                        <td className="border px-2 py-1 text-[12px] text-center">
                                          {memberCount === 0 ? (
                                            <span className="text-gray-400">-</span>
                                          ) : (
                                            <Popover
                                              trigger="click"
                                              placement="left"
                                              title="Manage Task Members"
                                              content={
                                                <TaskMemberManagerModal
                                                  taskId={task.ItemId}
                                                  memberIds={memberIds}
                                                  Employee={Employee}
                                                  projectMembers={selectedAssignedToMembers}
                                                  onRefresh={() => refreshModalData(projid)}
                                                  Timesheetapi={Timesheetapi}
                                                  toast={toast}
                                                />
                                              }
                                            >
                                              <span style={{ display: "inline-flex", justifyContent: "center", color: "#1a56db", padding: "2px 10px", fontWeight: 500, fontSize: 12, cursor: "pointer", minWidth: 36, background: "transparent" }}>
                                                {memberCount}
                                              </span>
                                            </Popover>
                                          )}
                                        </td>
                                        <td className="border px-2 py-1 text-[12px]">
                                          <div className="flex gap-3">
                                            <div className="text-blue-600 hover:underline cursor-pointer"
                                              onClick={(e) => { e.preventDefault(); handleEditTask(task.ItemId); }}>
                                              <FaRegEdit className="text-[16px]" />
                                            </div>
                                            <div className="text-red-600 hover:underline cursor-pointer"
                                              onClick={(e) => { e.preventDefault(); toggleTaskDelete(task); }}>
                                              <FaTrashAlt className="text-[15px]" />
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={11 + (showTrackColumn ? 1 : 0) + (showModuleColumn ? 1 : 0)} className="border p-2 text-center text-[13px]">
                                      {taskSearchText.trim()
                                        ? "No tasks match your search"
                                        : taskView === "completed" ? "No completed tasks yet" : "No open or in-progress tasks"}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>










                        </div>
                      </div>
                    </Spin>
                  )}

                  {/* Form action buttons — always visible */}
                  <div className="text-right mt-5">
                    <Button
                      className="w-[100px]"
                      type="default"
                      onClick={toggleModal}
                      style={{ marginRight: "8px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="w-[100px]"
                      type="primary"
                      htmlType="submit"
                      loading={projectload}
                    // onClick={() => {
                    //   if (edit) {
                    //     alert("Update clicked");
                    //   }
                    // }}
                    >
                      {edit
                        ? projectload
                          ? "Updating..."
                          : "Update"
                        : projectload
                          ? "Creating..."
                          : "Create"}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </AntdModal>
        )}

        {isModalOpenclient && (
          <Modal
            toggleModalclient={toggleModalclient}
            closable
            maskClosable={false}
            keyboard={false}
          >
            <div className="bg-white rounded-lg">
              <h3 className="text-[14px] font-[600] mb-2">{"Add a Client"}</h3>
              <Table columns={columnsclient} dataSource={dataclient} />
            </div>
          </Modal>
        )}

        {localStorage.getItem("emp_role_id") == 3 && (
          <div>
            <AntdModal
              title={
                <>
                  <ExclamationCircleFilled style={{ color: "orange", marginRight: "8px" }} />
                  {" "}Confirm
                </>
              }
              visible={projectdeletemodal}
              onOk={confirmDelete}
              onCancel={cancelDelete}
              closable
              maskClosable={false}
              keyboard={false}
              footer={[
                <Button key="cancel" onClick={cancelDelete}>Cancel</Button>,
                <Button key="confirm" type="primary" onClick={confirmDelete}>Delete</Button>,
              ]}
            >
              <p>
                Are you sure you want to delete this project? Deleting the project will also
                remove the assigned tasks for employees
              </p>
            </AntdModal>
          </div>
        )}

        {/* ==================== TASK DELETE CONFIRMATION MODAL ==================== */}
        {localStorage.getItem("emp_role_id") == "3" && (
          <AntdModal
            title={
              <>
                <ExclamationCircleFilled style={{ color: "orange", marginRight: "8px" }} />
                Confirm Task Deletion
              </>
            }
            visible={taskDeleteModal}
            onOk={confirmTaskDelete}
            onCancel={cancelTaskDelete}
            closable
            maskClosable={false}
            keyboard={false}
            okText="Yes, Delete Task"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"

            // 🔥 THIS IS THE MOST IMPORTANT FIX
            zIndex={3000}           // Higher than the main modal

            // Optional: Make it look better
            width={420}
            centered
          >
            <div className="py-2">
              <p className="text-[14px]">Are you sure you want to delete this task?</p>
              <p className="font-semibold mt-2 text-[14px] text-gray-800">
                "{taskToDelete?.Task_name || taskToDelete?.Item_Name || "this task"}"
              </p>
              {/* <p className="text-red-600 text-sm mt-4">
        This will perform a soft delete. The task will no longer appear in the list.
      </p> */}
            </div>
          </AntdModal>
        )}

        {/* Projects table */}




        {projectload ? (
          <Spin
            tip="Loading..."
            className="justify-center items-center flex"
            style={{ height: "100vh" }}
          >
            <div className="mt-[-25px] overflow-auto custom-table">
              <CustomTable
                component={"Project Create"}
                columns={columns}
                key={data.length}
                data={data}
                pinColumn={["action"]}
              />
            </div>
          </Spin>
        ) : (
          <div className="mt-[-25px] overflow-auto custom-table">
            <CustomTable
              component={"Project Create"}
              columns={columns}
              key={data.length}
              data={data}
              pinColumn={["action"]}
            />
          </div>
        )}
      </div>
    </div>
  );
};